"use client";

import { productDetails } from "@/data/product-details";

import dynamic from "next/dynamic";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clock3,
  Coffee,
  Gift,
  Home,
  Info,
  MapPin,
  Minus,
  Plus,
  ReceiptText,
  ShoppingBag,
  Star,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  categories,
  popularIds,
  products,
  type Product,
} from "@/data/catalog";

import {
  activePickupPoints,
  defaultPickupPoint,
  findActivePickupPoint,
  findActivePickupPointByName,
  type PickupPoint,
} from "@/data/points";

import ProductPhoto from "@/components/ProductPhoto";

const PickupMap = dynamic(
  () => import("@/components/PickupMap"),
  {
    ssr: false,
    loading: () => (
      <div className="h-[330px] animate-pulse rounded-[24px] bg-[#e9e3dc]" />
    ),
  },
);

type AppTab = "menu" | "points" | "orders" | "bonuses";

type CartAddon = {
  id: string;
  label: string;
  price: number;
};

type CartLine = {
  key: string;
  productId: string;
  name: string;
  image: string;
  sizeId: string;
  sizeLabel: string;
  addons: CartAddon[];
  unitPrice: number;
  qty: number;
};

// COFFEETRACK_CLIENT_PAYMENT_MODEL_V325

type ClientPaymentStatus =
  | "pending"
  | "paid"
  | "failed"
  | "cancelled";

type CompactOrder = {
  id: string;
  customerToken?: string;
  number: string;
  total: number;
  status: string;
  paymentStatus?: ClientPaymentStatus;
  pointId?: string;
  pointName?: string;
  createdAt?: string;
  phone?: string;
  paymentMethod?:
    | "card"
    | "sbp"
    | "sberpay";
  items?: {
    productId?: string;
    name: string;
    qty: number;
    unitPrice: number;
    sizeId?: string;
    sizeLabel?: string;
    addonIds?: string[];
    addons?: string[];
    rewardQty?: number;
    rewardBaseDiscount?: number;
  }[];
};

type PendingPaymentHandoff = {
  checkoutKey: string;
  resumeKey: string;
  order: CompactOrder;
  redirectUrl?: string;
  createdAt: string;
};

type CustomerSessionAccount = {
  id: string;
  phone: string;
  displayName?: string;
  createdAt: string;
};

type LoyaltySummary = {
  phone: string;
  completedCoffeeCount: number;
  completedOrders: number;
  totalOrders: number;
  target: number;
  progress: number;
  remaining: number;
  rewardsEarned: number;
  rewardsAvailable: number;
  rewardsUsed: number;
};

const money = (value: number) =>
  new Intl.NumberFormat("ru-RU").format(value) + " ₽";

// COFFEETRACK_CHECKOUT_KEY_HELPERS_V325

const CHECKOUT_KEY_STORAGE =
  "coffeetrack:checkout-key";

// COFFEETRACK_CHECKOUT_RESUME_KEY_V326
const CHECKOUT_RESUME_STORAGE =
  "coffeetrack:checkout-resume";

const PENDING_PAYMENT_STORAGE =
  "coffeetrack:pending-payment";

const createCheckoutKey = () => {
  const bytes =
    new Uint8Array(24);

  window.crypto.getRandomValues(
    bytes,
  );

  return Array.from(
    bytes,
  )
    .map(
      (value) =>
        value
          .toString(16)
          .padStart(2, "0"),
    )
    .join("");
};

const isCheckoutKey = (
  value: unknown,
): value is string =>
  typeof value === "string" &&
  /^[A-Za-z0-9_-]{20,128}$/.test(
    value,
  );

// COFFEETRACK_ORDER_HISTORY_HELPERS_V34
const orderHistoryStatusMeta = (
  status: string,
) => {
  if (status === "paid") {
    return {
      label: "Ждёт принятия",
      className:
        "bg-[#fff1c3] text-[#765b21]",
    };
  }

  if (status === "accepted") {
    return {
      label: "Готовится",
      className:
        "bg-[#e9eef8] text-[#3e5f91]",
    };
  }

  if (status === "ready") {
    return {
      label: "Можно забирать",
      className:
        "bg-[#e5f5e8] text-[#26763d]",
    };
  }

  if (status === "completed") {
    return {
      label: "Выдан",
      className:
        "bg-[#edf4ed] text-[#52735a]",
    };
  }

  if (status === "cancelled") {
    return {
      label: "Отменён",
      className:
        "bg-[#f7e8e5] text-[#9a4d43]",
    };
  }

  return {
    label: "Заказ",
    className:
      "bg-[#f1ece6] text-[#776b63]",
  };
};

const formatOrderHistoryDate = (
  value?: string,
) => {
  if (!value) {
    return "Дата не указана";
  }

  const date =
    new Date(value);

  if (
    !Number.isFinite(
      date.getTime(),
    )
  ) {
    return "Дата не указана";
  }

  return date.toLocaleString(
    "ru-RU",
    {
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
};

const normalizePhoneDigits = (value: string) => {
  let digits = value.replace(/\D/g, "");

  if (digits.startsWith("8")) {
    digits = "7" + digits.slice(1);
  }

  if (
    digits.length > 0 &&
    !digits.startsWith("7")
  ) {
    digits = "7" + digits;
  }

  return digits.slice(0, 11);
};

const formatPhone = (value: string) => {
  const digits = normalizePhoneDigits(value);

  if (!digits.length) return "";

  const rest = digits.slice(1);

  let result = "+7";

  if (rest.length > 0) {
    result += " " + rest.slice(0, 3);
  }

  if (rest.length > 3) {
    result += " " + rest.slice(3, 6);
  }

  if (rest.length > 6) {
    result += "-" + rest.slice(6, 8);
  }

  if (rest.length > 8) {
    result += "-" + rest.slice(8, 10);
  }

  return result;
};

export default function HomePage() {
  const [tab, setTab] = useState<AppTab>("menu");
  const [category, setCategory] = useState("popular");

  const [pickupPoint, setPickupPoint] =
    useState<PickupPoint>(defaultPickupPoint);

  const [selectedProduct, setSelectedProduct] =
    useState<Product | null>(null);

  const [productInfoOpen, setProductInfoOpen] =
    useState(false);

  const [paymentMethod, setPaymentMethod] =
    useState<"card" | "sbp" | "sberpay">("card");

  const [customerPhone, setCustomerPhone] =
    useState("");

  const [phoneError, setPhoneError] =
    useState("");

  const [loyalty, setLoyalty] =
    useState<LoyaltySummary | null>(
      null,
    );

  const [
    loyaltyLoading,
    setLoyaltyLoading,
  ] = useState(false);

  const [
    profilePhoneMessage,
    setProfilePhoneMessage,
  ] = useState("");

  // COFFEETRACK_CUSTOMER_SESSION_STATE_V38A
  const [
    customerSession,
    setCustomerSession,
  ] =
    useState<CustomerSessionAccount | null>(
      null,
    );

  const [
    customerSessionLoading,
    setCustomerSessionLoading,
  ] = useState(true);

  const selectedProductDetails =
    selectedProduct
      ? productDetails[selectedProduct.id]
      : undefined;

  const selectedNutrition =
    selectedProductDetails?.nutrition;

  const selectedAllergens =
    selectedProductDetails?.allergens ?? [];

  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [selectedAddonIds, setSelectedAddonIds] =
    useState<string[]>([]);

  const [productQty, setProductQty] = useState(1);

  const [cart, setCart] = useState<CartLine[]>([]);

  const [cartStorageReady, setCartStorageReady] =
    useState(false);

  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const [placingOrder, setPlacingOrder] = useState(false);

  const [currentOrder, setCurrentOrder] =
    useState<CompactOrder | null>(null);

  // COFFEETRACK_ORDER_HISTORY_STATE_V34
  const [
    orderHistory,
    setOrderHistory,
  ] = useState<CompactOrder[]>([]);

  const [
    orderHistoryLoading,
    setOrderHistoryLoading,
  ] = useState(false);

  const [
    orderHistoryError,
    setOrderHistoryError,
  ] = useState("");

  const [orderSuccessOpen, setOrderSuccessOpen] =
    useState(false);

  // COFFEETRACK_MODAL_SCROLL_LOCK_V316
  useEffect(() => {
    const modalOpen =
      Boolean(
        selectedProduct ||
        cartOpen ||
        checkoutOpen ||
        orderSuccessOpen,
      );

    if (!modalOpen) {
      return;
    }

    const root =
      document.documentElement;

    const body =
      document.body;

    const previousRootOverflow =
      root.style.overflow;

    const previousBodyOverflow =
      body.style.overflow;

    root.style.overflow =
      "hidden";

    body.style.overflow =
      "hidden";

    return () => {
      root.style.overflow =
        previousRootOverflow;

      body.style.overflow =
        previousBodyOverflow;
    };
  }, [
    selectedProduct,
    cartOpen,
    checkoutOpen,
    orderSuccessOpen,
  ]);

  // COFFEETRACK_LOYALTY_REWARD_UI_V33B
  const [
    useLoyaltyReward,
    setUseLoyaltyReward,
  ] = useState(false);

  // COFFEETRACK_PAYMENT_RETURN_RECOVERY_V325
  //
  // pending-payment хранится отдельно
  // от обычного last-order.
  //
  // Это позволяет пережить переход
  // на страницу банка и возврат обратно.
  //
  useEffect(() => {
    let cancelled =
      false;

    let handoff:
      PendingPaymentHandoff |
      null =
        null;

    try {
      const raw =
        localStorage.getItem(
          PENDING_PAYMENT_STORAGE,
        );

      if (raw) {
        const parsed =
          JSON.parse(raw);

        if (
          parsed &&
          isCheckoutKey(
            parsed.checkoutKey,
          ) &&
          isCheckoutKey(
            parsed.resumeKey,
          ) &&
          parsed.order &&
          typeof parsed.order.id ===
            "string"
        ) {
          handoff = {
            checkoutKey:
              parsed.checkoutKey,

            resumeKey:
              parsed.resumeKey,

            order:
              parsed.order,

            redirectUrl:
              typeof parsed.redirectUrl ===
                "string"
                ? parsed.redirectUrl
                : undefined,

            createdAt:
              typeof parsed.createdAt ===
                "string"
                ? parsed.createdAt
                : new Date().toISOString(),
          };
        }
      }
    } catch {}


    if (!handoff) {
      return;
    }


    /*
     * Даже если пользователь просто
     * закрыл вкладку и вернулся позже,
     * восстанавливаем pending order.
     */
    setCurrentOrder(
      handoff.order,
    );


    const params =
      new URLSearchParams(
        window.location.search,
      );

    const isPaymentReturn =
      params.get(
        "payment_return",
      ) ===
      "1";


    if (!isPaymentReturn) {
      return;
    }


    const cleanPaymentReturnUrl =
      () => {
        const url =
          new URL(
            window.location.href,
          );

        url.searchParams.delete(
          "payment_return",
        );

        window.history.replaceState(
          window.history.state,
          "",
          (
            url.pathname +
            url.search +
            url.hash
          ),
        );
      };


    const refresh =
      async () => {
        try {
          const token =
            handoff?.order
              .customerToken;

          const response =
            await fetch(
              `/api/orders/${handoff?.order.id}`,
              {
                cache:
                  "no-store",

                credentials:
                  "same-origin",

                headers:
                  token
                    ? {
                        "x-coffeetrack-order-token":
                          token,
                      }
                    : undefined,
              },
            );


          if (!response.ok) {
            return;
          }


          const data =
            await response.json();


          if (
            cancelled ||
            !data.order
          ) {
            return;
          }


          const next:
            CompactOrder = {
              id:
                data.order.id,

              customerToken:
                token ||
                undefined,

              number:
                data.order.number,

              total:
                data.order.total,

              status:
                data.order.status,

              paymentStatus:
                data.order.paymentStatus,

              pointId:
                data.order.pointId,

              pointName:
                data.order.pointName,

              createdAt:
                data.order.createdAt,

              phone:
                data.order.phone,

              paymentMethod:
                data.order.paymentMethod,

              items:
                Array.isArray(
                  data.order.items,
                )
                  ? data.order.items
                  : [],
            };


          setCurrentOrder(
            next,
          );


          try {
            localStorage.setItem(
              "coffeetrack:last-order",
              JSON.stringify(
                next,
              ),
            );

            localStorage.setItem(
              PENDING_PAYMENT_STORAGE,
              JSON.stringify({
                ...handoff,

                order:
                  next,
              }),
            );
          } catch {}


          if (
            next.paymentStatus ===
            "pending"
          ) {
            alert(
              "Платёж обрабатывается. Статус заказа обновится автоматически.",
            );
          }


          if (
            next.paymentStatus ===
              "failed" ||
            next.paymentStatus ===
              "cancelled"
          ) {
            try {
              localStorage.removeItem(
                PENDING_PAYMENT_STORAGE,
              );

              localStorage.removeItem(
                CHECKOUT_KEY_STORAGE,
              );

              // COFFEETRACK_RESUME_FAILED_CLEANUP_V3261
              localStorage.removeItem(
                CHECKOUT_RESUME_STORAGE,
              );
            } catch {}

            alert(
              "Оплата не завершена. Корзина сохранена — можно попробовать ещё раз.",
            );
          }


          cleanPaymentReturnUrl();

        } catch {}
      };


    refresh();


    return () => {
      cancelled =
        true;
    };
  }, []);


  // COFFEETRACK_PAYMENT_STORAGE_V20
  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(
          "coffeetrack:payment-method",
        );

      if (
        saved === "card" ||
        saved === "sbp" ||
        saved === "sberpay"
      ) {
        setPaymentMethod(saved);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        "coffeetrack:payment-method",
        paymentMethod,
      );
    } catch {}
  }, [paymentMethod]);

  // COFFEETRACK_CART_STORAGE_V20
  useEffect(() => {
    try {
      const raw = localStorage.getItem(
        "coffeetrack:cart",
      );

      if (raw) {
        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed)) {
          const restored: CartLine[] =
            parsed
              .filter(
                (line) =>
                  line &&
                  typeof line.productId === "string" &&
                  typeof line.name === "string" &&
                  Number.isFinite(
                    Number(line.unitPrice),
                  ) &&
                  Number(line.qty) > 0,
              )
              .map((line) => ({
                key: String(
                  line.key ||
                    `${line.productId}|base|`,
                ),
                productId: String(
                  line.productId,
                ),
                name: String(line.name),
                image: String(
                  line.image || "",
                ),
                sizeId: String(
                  line.sizeId ||
                    String(
                      line.key || "",
                    ).split("|")[1] ||
                    "base",
                ),
                sizeLabel: String(
                  line.sizeLabel || "",
                ),
                addons: Array.isArray(
                  line.addons,
                )
                  ? line.addons
                      .filter(
                        (addon: unknown) =>
                          addon &&
                          typeof addon ===
                            "object",
                      )
                      .map(
                        (
                          addon: {
                            id?: unknown;
                            label?: unknown;
                            price?: unknown;
                          },
                        ) => ({
                          id: String(
                            addon.id || "",
                          ),
                          label: String(
                            addon.label || "",
                          ),
                          price: Number(
                            addon.price || 0,
                          ),
                        }),
                      )
                  : [],
                unitPrice: Number(
                  line.unitPrice,
                ),
                qty: Math.max(
                  1,
                  Math.floor(
                    Number(line.qty),
                  ),
                ),
              }));

          if (restored.length) {
            setCart(restored);
          }
        }
      }
    } catch {
      try {
        localStorage.removeItem(
          "coffeetrack:cart",
        );
      } catch {}
    } finally {
      setCartStorageReady(true);
    }
  }, []);

  useEffect(() => {
    if (!cartStorageReady) return;

    try {
      if (cart.length) {
        localStorage.setItem(
          "coffeetrack:cart",
          JSON.stringify(cart),
        );
      } else {
        localStorage.removeItem(
          "coffeetrack:cart",
        );
      }
    } catch {}
  }, [cart, cartStorageReady]);

  useEffect(() => {
    try {
      const savedPointId = localStorage.getItem(
        "coffeetrack:pickup-point",
      );

      // COFFEETRACK_POINT_RESTORE_V319
      const savedPoint =
        savedPointId
          ? findActivePickupPoint(
              savedPointId,
            )
          : undefined;

      if (savedPoint) {
        setPickupPoint(savedPoint);
      }

      const savedOrder = localStorage.getItem(
        "coffeetrack:last-order",
      );

      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);

        if (
          parsed?.id &&
          parsed?.number &&
          typeof parsed.customerToken === "string" &&
          parsed.customerToken.length >= 32
        ) {
          setCurrentOrder(parsed);
        } else {
          localStorage.removeItem(
            "coffeetrack:last-order",
          );
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const savedPhone = localStorage.getItem(
        "coffeetrack:customer-phone",
      );

      if (savedPhone) {
        setCustomerPhone(
          formatPhone(savedPhone),
        );
      }
    } catch {}
  }, []);

  // COFFEETRACK_CUSTOMER_SESSION_SYNC_V38A
  useEffect(() => {
    let cancelled =
      false;

    const syncSession =
      async () => {
        setCustomerSessionLoading(
          true,
        );

        try {
          /*
           * Сначала проверяем уже существующую
           * httpOnly cookie.
           */
          const existing =
            await fetch(
              "/api/customer/session",
              {
                cache:
                  "no-store",
              },
            );

          if (existing.ok) {
            const data =
              await existing.json();

            if (!cancelled) {
              setCustomerSession(
                data.account ??
                  null,
              );
            }

            return;
          }

          /*
           * Старый browser proof:
           * token последнего заказа.
           *
           * Если он есть, один раз меняем его
           * на нормальную серверную сессию.
           */
          const orderToken =
            currentOrder?.customerToken;

          if (!orderToken) {
            if (!cancelled) {
              setCustomerSession(
                null,
              );
            }

            return;
          }

          const created =
            await fetch(
              "/api/customer/session",
              {
                method:
                  "POST",

                headers: {
                  "x-coffeetrack-order-token":
                    orderToken,
                },
              },
            );

          if (!created.ok) {
            if (!cancelled) {
              setCustomerSession(
                null,
              );
            }

            return;
          }

          const data =
            await created.json();

          if (!cancelled) {
            setCustomerSession(
              data.account ??
                null,
            );
          }

        } catch {
          if (!cancelled) {
            setCustomerSession(
              null,
            );
          }

        } finally {
          if (!cancelled) {
            setCustomerSessionLoading(
              false,
            );
          }
        }
      };

    syncSession();

    return () => {
      cancelled = true;
    };
  }, [
    currentOrder?.customerToken,
  ]);

  // COFFEETRACK_SESSION_PHONE_SYNC_V38B
  useEffect(() => {
    const phone =
      customerSession?.phone;

    if (!phone) {
      return;
    }

    setCustomerPhone(
      formatPhone(
        phone,
      ),
    );

    try {
      localStorage.setItem(
        "coffeetrack:customer-phone",
        phone,
      );
    } catch {}
  }, [
    customerSession?.phone,
  ]);

  // COFFEETRACK_LOYALTY_FETCH_V23
  // COFFEETRACK_LOYALTY_SESSION_FIRST_V38B
  useEffect(() => {
    if (
      tab !== "bonuses" &&
      tab !== "menu" &&
      !checkoutOpen
    ) {
      return;
    }

    const customerToken =
      currentOrder?.customerToken;

    const hasSession =
      Boolean(
        customerSession?.id,
      );

    let legacyPhone =
      "";

    if (!hasSession) {
      const digits =
        normalizePhoneDigits(
          customerPhone,
        );

      if (
        digits.length !== 11 ||
        !digits.startsWith("7") ||
        !customerToken
      ) {
        setLoyalty(null);
        setLoyaltyLoading(false);
        return;
      }

      legacyPhone =
        `+${digits}`;
    }

    let cancelled =
      false;

    const loadLoyalty =
      async () => {
        setLoyaltyLoading(
          true,
        );

        try {
          const url =
            hasSession
              ? "/api/loyalty"
              : `/api/loyalty?phone=${encodeURIComponent(
                  legacyPhone,
                )}`;

          const response =
            await fetch(
              url,
              {
                cache:
                  "no-store",

                credentials:
                  "same-origin",

                headers:
                  !hasSession &&
                  customerToken
                    ? {
                        "x-coffeetrack-order-token":
                          customerToken,
                      }
                    : undefined,
              },
            );

          const data =
            await response.json();

          if (cancelled) {
            return;
          }

          if (!response.ok) {
            setLoyalty(
              null,
            );

            return;
          }

          setLoyalty(
            data.loyalty ??
              null,
          );

        } catch {
          if (!cancelled) {
            setLoyalty(
              null,
            );
          }

        } finally {
          if (!cancelled) {
            setLoyaltyLoading(
              false,
            );
          }
        }
      };

    loadLoyalty();

    const timer =
      window.setInterval(
        loadLoyalty,
        5000,
      );

    return () => {
      cancelled = true;

      window.clearInterval(
        timer,
      );
    };
  }, [
    tab,
    checkoutOpen,
    customerPhone,
    customerSession?.id,
    currentOrder?.customerToken,
  ]);

    // COFFEETRACK_ACTIVE_ORDER_SESSION_FIRST_V38C
  useEffect(() => {
    const customerToken =
      currentOrder?.customerToken;

    const hasSession =
      Boolean(
        customerSession?.id,
      );

    if (
      !currentOrder?.id ||
      (
        !hasSession &&
        !customerToken
      )
    ) {
      return;
    }

    const refresh =
      async () => {
        try {
          const response =
            await fetch(
              `/api/orders/${currentOrder.id}`,
              {
                cache:
                  "no-store",

                credentials:
                  "same-origin",

                headers:
                  !hasSession &&
                  customerToken
                    ? {
                        "x-coffeetrack-order-token":
                          customerToken,
                      }
                    : undefined,
              },
            );

          if (!response.ok) {
            return;
          }

          const data =
            await response.json();

          if (!data.order) {
            return;
          }

          const next:
            CompactOrder = {
              id:
                data.order.id,

              customerToken:
                customerToken ||
                undefined,

              number:
                data.order.number,

              total:
                data.order.total,

              status:
                data.order.status,

              // COFFEETRACK_ACTIVE_PAYMENT_STATUS_V325
              paymentStatus:
                data.order.paymentStatus,

              pointId:
                data.order.pointId,

              pointName:
                data.order.pointName,

              createdAt:
                data.order.createdAt,

              phone:
                data.order.phone,

              paymentMethod:
                data.order.paymentMethod,

              items:
                Array.isArray(
                  data.order.items,
                )
                  ? data.order.items
                  : [],
            };

          setCurrentOrder(
            next,
          );

          try {
            localStorage.setItem(
              "coffeetrack:last-order",
              JSON.stringify(
                next,
              ),
            );
          } catch {}

        } catch {}
      };

    refresh();

    const timer =
      window.setInterval(
        refresh,
        2500,
      );

    return () =>
      window.clearInterval(
        timer,
      );
  }, [
    currentOrder?.id,
    currentOrder?.customerToken,
    customerSession?.id,
  ]);

  // COFFEETRACK_PAYMENT_PAID_TRANSITION_V325
  //
  // LIVE:
  // корзина очищается и success показывается
  // только после paymentStatus=paid.
  //
  // TEST:
  // pending-payment отсутствует, поэтому
  // существующая логика никак не меняется.
  //
  useEffect(() => {
    if (
      !currentOrder?.id ||
      currentOrder.paymentStatus !==
        "paid"
    ) {
      return;
    }


    let matchesPending =
      false;

    try {
      const raw =
        localStorage.getItem(
          PENDING_PAYMENT_STORAGE,
        );

      if (raw) {
        const parsed =
          JSON.parse(raw);

        matchesPending =
          parsed?.order?.id ===
          currentOrder.id;
      }
    } catch {}


    if (!matchesPending) {
      return;
    }


    try {
      localStorage.removeItem(
        PENDING_PAYMENT_STORAGE,
      );

      localStorage.removeItem(
        CHECKOUT_KEY_STORAGE,
      );

      localStorage.removeItem(
        CHECKOUT_RESUME_STORAGE,
      );

      localStorage.setItem(
        "coffeetrack:last-order",
        JSON.stringify(
          currentOrder,
        ),
      );
    } catch {}


    setCart([]);
    setUseLoyaltyReward(false);
    setCheckoutOpen(false);
    setCartOpen(false);
    setOrderSuccessOpen(true);


    /*
     * Если webhook пришёл очень быстро,
     * параметр возврата тоже убираем.
     */
    const url =
      new URL(
        window.location.href,
      );

    if (
      url.searchParams.has(
        "payment_return",
      )
    ) {
      url.searchParams.delete(
        "payment_return",
      );

      window.history.replaceState(
        window.history.state,
        "",
        (
          url.pathname +
          url.search +
          url.hash
        ),
      );
    }
  }, [
    currentOrder?.id,
    currentOrder?.paymentStatus,
  ]);


  // COFFEETRACK_ORDER_HISTORY_FETCH_V34
  // COFFEETRACK_ORDER_HISTORY_SESSION_FIRST_V38B
  useEffect(() => {
    // COFFEETRACK_PENDING_ORDER_ACTIVE_V325
    const currentIsActive =
      currentOrder?.paymentStatus ===
        "pending" ||
      currentOrder?.status === "paid" ||
      currentOrder?.status === "accepted" ||
      currentOrder?.status === "ready";

    const needsSessionRecovery =
      Boolean(
        customerSession?.id,
      ) &&
      !currentIsActive;

    if (
      tab !== "orders" &&
      !needsSessionRecovery
    ) {
      return;
    }

    const customerToken =
      currentOrder?.customerToken;

    const hasSession =
      Boolean(
        customerSession?.id,
      );

    if (
      !hasSession &&
      !customerToken
    ) {
      setOrderHistory([]);
      setOrderHistoryError("");
      setOrderHistoryLoading(false);
      return;
    }

    let cancelled =
      false;

    const loadHistory =
      async () => {
        setOrderHistoryLoading(
          true,
        );

        setOrderHistoryError(
          "",
        );

        try {
          const response =
            await fetch(
              "/api/orders/history",
              {
                cache:
                  "no-store",

                credentials:
                  "same-origin",

                headers:
                  !hasSession &&
                  customerToken
                    ? {
                        "x-coffeetrack-order-token":
                          customerToken,
                      }
                    : undefined,
              },
            );

          const data =
            await response.json();

          if (cancelled) {
            return;
          }

          if (!response.ok) {
            setOrderHistory(
              [],
            );

            setOrderHistoryError(
              data.error ||
                "Не удалось загрузить историю",
            );

            return;
          }

          const orders:
            CompactOrder[] =
            Array.isArray(
              data.orders,
            )
              ? data.orders
              : [];

          setOrderHistory(
            orders,
          );

          /*
           * COFFEETRACK_ACTIVE_ORDER_RECOVERY_V38C
           *
           * После будущего SMS-входа новый браузер
           * не имеет legacy customerToken.
           * Находим текущий заказ внутри защищённой
           * session-history и восстанавливаем его.
           */
          if (
            hasSession &&
            !currentIsActive
          ) {
            const activeOrder =
              orders.find(
                (order) =>
                  order.paymentStatus ===
                    "pending" ||
                  order.status === "paid" ||
                  order.status === "accepted" ||
                  order.status === "ready",
              );

            if (activeOrder) {
              const recovered:
                CompactOrder = {
                  ...activeOrder,

                  customerToken:
                    undefined,
                };

              setCurrentOrder(
                recovered,
              );

              try {
                localStorage.setItem(
                  "coffeetrack:last-order",
                  JSON.stringify(
                    recovered,
                  ),
                );
              } catch {}
            }
          }

        } catch {
          if (!cancelled) {
            setOrderHistory(
              [],
            );

            setOrderHistoryError(
              "Не удалось загрузить историю",
            );
          }

        } finally {
          if (!cancelled) {
            setOrderHistoryLoading(
              false,
            );
          }
        }
      };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [
    tab,
    customerSession?.id,
    currentOrder?.id,
    currentOrder?.status,
    currentOrder?.paymentStatus,
    currentOrder?.customerToken,
  ]);

  const visibleProducts = useMemo(() => {
    if (category === "popular") {
      return popularIds
        .map((id) =>
          products.find((product) => product.id === id),
        )
        .filter(
          (product): product is Product => Boolean(product),
        );
    }

    return products.filter(
      (product) => product.category === category,
    );
  }, [category]);

  const cartCount = cart.reduce(
    (sum, line) => sum + line.qty,
    0,
  );

  const cartTotal = cart.reduce(
    (sum, line) => sum + line.unitPrice * line.qty,
    0,
  );

  const rewardPreviewBasePrice =
    cart.reduce(
      (
        highest,
        line,
      ) => {
        if (line.qty < 1) {
          return highest;
        }

        const product =
          products.find(
            (item) =>
              item.id ===
              line.productId,
          );

        if (
          product?.category !==
          "coffee"
        ) {
          return highest;
        }

        return Math.max(
          highest,
          product.basePrice,
        );
      },
      0,
    );

  // COFFEETRACK_REWARD_SESSION_CLIENT_V38C
  const rewardCanApply =
    Boolean(
      loyalty?.rewardsAvailable &&
      rewardPreviewBasePrice > 0 &&
      (
        customerSession?.id ||
        currentOrder?.customerToken
      ),
    );

  const rewardPreviewDiscount =
    useLoyaltyReward &&
    rewardCanApply
      ? rewardPreviewBasePrice
      : 0;

  const checkoutTotal =
    Math.max(
      0,
      cartTotal -
        rewardPreviewDiscount,
    );

  const paymentMethodLabel =
    paymentMethod === "sbp"
      ? "СБП"
      : paymentMethod === "sberpay"
        ? "SberPay"
        : "Банковская карта";

  useEffect(() => {
    if (
      useLoyaltyReward &&
      !rewardCanApply
    ) {
      setUseLoyaltyReward(false);
    }
  }, [
    useLoyaltyReward,
    rewardCanApply,
  ]);

  const selectedSize =
    selectedProduct?.sizes?.find(
      (size) => size.id === selectedSizeId,
    ) ?? null;

  const selectedAddons =
    selectedProduct?.addons?.filter((addon) =>
      selectedAddonIds.includes(addon.id),
    ) ?? [];

  const configuredUnitPrice = selectedProduct
    ? selectedProduct.basePrice +
      (selectedSize?.delta ?? 0) +
      selectedAddons.reduce(
        (sum, addon) => sum + addon.price,
        0,
      )
    : 0;

  const productCartQty = (
    productId: string,
  ) =>
    cart.reduce(
      (sum, line) =>
        line.productId === productId
          ? sum + line.qty
          : sum,
      0,
    );

  const openProduct = (product: Product) => {
    setSelectedProduct(product);
    setSelectedSizeId(product.sizes?.[0]?.id ?? "base");
    setSelectedAddonIds([]);
    setProductQty(1);
  };

  const quickAddProduct = (product: Product) => {
    //
    // "+" всегда означает быстрое добавление.
    // Если есть размеры — берём первый (базовый).
    // Добавки по быстрому "+" не выбираем.
    //
    const defaultSize =
      product.sizes?.[0] ?? null;

    const sizeId =
      defaultSize?.id ?? "base";

    const sizeLabel =
      defaultSize?.label ?? "";

    const unitPrice =
      product.basePrice +
      (defaultSize?.delta ?? 0);

    const key =
      `${product.id}|${sizeId}|`;

    setCart((current) => {
      const existing = current.find(
        (line) => line.key === key,
      );

      if (existing) {
        return current.map((line) =>
          line.key === key
            ? {
                ...line,
                qty: line.qty + 1,
              }
            : line,
        );
      }

      return [
        ...current,
        {
          key,
          productId: product.id,
          name: product.name,
          image: product.image,
          sizeId,
          sizeLabel,
          addons: [],
          unitPrice,
          qty: 1,
        },
      ];
    });

    //
    // Небольшой тактильный отклик там,
    // где браузер его поддерживает.
    //
    try {
      navigator.vibrate?.(12);
    } catch {}
  };

  // COFFEETRACK_TAB_BROWSER_HISTORY_V317
  const tabHash = (
    nextTab: AppTab,
  ) => {
    if (nextTab === "points") {
      return "#points";
    }

    if (nextTab === "orders") {
      return "#orders";
    }

    if (nextTab === "bonuses") {
      return "#profile";
    }

    return "";
  };

  const tabFromBrowser = (): AppTab => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "menu";
    }

    const hash =
      window.location.hash;

    if (hash === "#points") {
      return "points";
    }

    if (hash === "#orders") {
      return "orders";
    }

    if (hash === "#profile") {
      return "bonuses";
    }

    return "menu";
  };

  const goToTab = (
    nextTab: AppTab,
  ) => {
    if (
      typeof window !==
      "undefined"
    ) {
      const nextHash =
        tabHash(nextTab);

      const url =
        `${window.location.pathname}` +
        `${window.location.search}` +
        nextHash;

      const alreadyThere =
        window.location.hash ===
        nextHash;

      if (!alreadyThere) {
        try {
          window.history.pushState(
            {
              coffeetrackTab:
                nextTab,
            },
            "",
            url,
          );
        } catch {}
      }
    }

    setTab(nextTab);
  };

  const openProfile = () => {
    goToTab("bonuses");
  };

  const navigateTab = (
    nextTab: AppTab,
  ) => {
    goToTab(nextTab);
  };

  useEffect(() => {
    const syncTabFromBrowser =
      () => {
        setTab(
          tabFromBrowser(),
        );
      };

    /*
     * Side buttons on a mouse,
     * browser Back / Forward,
     * trackpad navigation.
     */
    window.addEventListener(
      "popstate",
      syncTabFromBrowser,
    );

    /*
     * Also restore the correct tab
     * when page is opened directly
     * with #points / #orders / #profile.
     */
    syncTabFromBrowser();

    return () => {
      window.removeEventListener(
        "popstate",
        syncTabFromBrowser,
      );
    };
  }, []);

  const toggleAddon = (id: string) => {
    setSelectedAddonIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const addProduct = () => {
    if (!selectedProduct) return;

    const sizeId = selectedSize?.id ?? "base";
    const addonKey = [...selectedAddonIds]
      .sort()
      .join(",");

    const key =
      `${selectedProduct.id}|${sizeId}|${addonKey}`;

    setCart((current) => {
      const existing = current.find(
        (line) => line.key === key,
      );

      if (existing) {
        return current.map((line) =>
          line.key === key
            ? {
                ...line,
                qty: line.qty + productQty,
              }
            : line,
        );
      }

      return [
        ...current,
        {
          key,
          productId: selectedProduct.id,
          name: selectedProduct.name,
          image: selectedProduct.image,
          sizeId,
          sizeLabel: selectedSize?.label ?? "",
          addons: selectedAddons,
          unitPrice: configuredUnitPrice,
          qty: productQty,
        },
      ];
    });

    setSelectedProduct(null);
  };

  const changeQty = (key: string, delta: number) => {
    setCart((current) =>
      current
        .map((line) =>
          line.key === key
            ? {
                ...line,
                qty: line.qty + delta,
              }
            : line,
        )
        .filter((line) => line.qty > 0),
    );
  };

  const choosePoint = (point: PickupPoint) => {
    setPickupPoint(point);

    try {
      localStorage.setItem(
        "coffeetrack:pickup-point",
        point.id,
      );
    } catch {}
  };

  // COFFEETRACK_CLIENT_PAYMENT_HANDOFF_V325
  const placeOrder = async () => {
    if (
      !cart.length ||
      placingOrder
    ) {
      return;
    }


    const phoneDigits =
      normalizePhoneDigits(
        customerPhone,
      );


    if (
      phoneDigits.length !== 11 ||
      !phoneDigits.startsWith("7")
    ) {
      setPhoneError(
        "Укажи номер телефона в формате +7 999 123-45-67",
      );

      return;
    }


    setPhoneError("");


    try {
      localStorage.setItem(
        "coffeetrack:customer-phone",
        `+${phoneDigits}`,
      );
    } catch {}


    /*
     * Один checkout attempt имеет один
     * стабильный ключ.
     *
     * В test-mode сервер его игнорирует.
     *
     * В live-mode этот же ключ уходит
     * локальной транзакции и provider-у.
     */
    let checkoutKey =
      "";

    try {
      const saved =
        localStorage.getItem(
          CHECKOUT_KEY_STORAGE,
        );

      if (
        isCheckoutKey(
          saved,
        )
      ) {
        checkoutKey =
          saved;
      }
    } catch {}


    if (!checkoutKey) {
      checkoutKey =
        createCheckoutKey();

      try {
        localStorage.setItem(
          CHECKOUT_KEY_STORAGE,
          checkoutKey,
        );
      } catch {}
    }


    // COFFEETRACK_CLIENT_RESUME_SECRET_V326
    //
    // Отдельный secret используется только
    // для CoffeeTrack resume и никогда
    // не отправляется provider-у.
    //
    let resumeKey =
      "";

    try {
      const savedResume =
        localStorage.getItem(
          CHECKOUT_RESUME_STORAGE,
        );

      if (
        isCheckoutKey(
          savedResume,
        )
      ) {
        resumeKey =
          savedResume;
      }
    } catch {}


    if (!resumeKey) {
      resumeKey =
        createCheckoutKey();

      try {
        localStorage.setItem(
          CHECKOUT_RESUME_STORAGE,
          resumeKey,
        );
      } catch {}
    }


    setPlacingOrder(
      true,
    );


    try {
      const response =
        await fetch(
          "/api/orders",
          {
            method:
              "POST",

            credentials:
              "same-origin",

            headers: {
              "Content-Type":
                "application/json",

              "x-coffeetrack-checkout-key":
                checkoutKey,

              "x-coffeetrack-checkout-resume":
                resumeKey,

              // COFFEETRACK_ORDER_POST_SESSION_V38C
              ...(
                !customerSession?.id &&
                currentOrder?.customerToken
                  ? {
                      "x-coffeetrack-order-token":
                        currentOrder.customerToken,
                    }
                  : {}
              ),
            },

            body:
              JSON.stringify({
                pointId:
                  pickupPoint.id,

                pointName:
                  pickupPoint.name,

                phone:
                  `+${phoneDigits}`,

                paymentMethod,

                useLoyaltyReward:
                  useLoyaltyReward &&
                  rewardCanApply,

                items:
                  cart.map(
                    (line) => ({
                      productId:
                        line.productId,

                      qty:
                        line.qty,

                      sizeId:
                        line.sizeId ||
                        undefined,

                      addonIds:
                        line.addons.map(
                          (addon) =>
                            addon.id,
                        ),
                    }),
                  ),
              }),
          },
        );


      const data =
        await response.json();


      // COFFEETRACK_TERMINAL_RETRY_CLEANUP_V3261
      //
      // failed/cancelled checkout можно
      // безопасно завершить локально:
      // следующая попытка получит новые keys.
      //
      if (
        !response.ok &&
        data?.code ===
          "CHECKOUT_TERMINAL"
      ) {
        try {
          localStorage.removeItem(
            CHECKOUT_KEY_STORAGE,
          );

          localStorage.removeItem(
            CHECKOUT_RESUME_STORAGE,
          );

          localStorage.removeItem(
            PENDING_PAYMENT_STORAGE,
          );
        } catch {}
      }


      /*
       * Даже 502 live-provider может
       * содержать уже созданный pending order.
       *
       * Сохраняем его ДО throw, чтобы не
       * потерять customerToken и связь
       * с локальным payment_intent.
       */
      if (
        data?.order?.id
      ) {
        const compact:
          CompactOrder = {
            id:
              data.order.id,

            customerToken:
              data.order.customerToken,

            number:
              data.order.number,

            total:
              data.order.total,

            status:
              data.order.status,

            paymentStatus:
              data.order.paymentStatus,

            pointId:
              data.order.pointId,

            pointName:
              data.order.pointName,

            createdAt:
              data.order.createdAt,

            phone:
              data.order.phone,

            paymentMethod:
              data.order.paymentMethod,

            items:
              Array.isArray(
                data.order.items,
              )
                ? data.order.items
                : [],
          };


        setCurrentOrder(
          compact,
        );


        try {
          localStorage.setItem(
            "coffeetrack:last-order",
            JSON.stringify(
              compact,
            ),
          );


          /*
           * Только live/pending ответ
           * считается payment handoff.
           */
          if (
            data.mode === "live" ||
            compact.paymentStatus ===
              "pending" ||
            data.retryable === true
          ) {
            const handoff:
              PendingPaymentHandoff = {
                checkoutKey,

                resumeKey,

                order:
                  compact,

                redirectUrl:
                  typeof data?.payment
                    ?.redirectUrl ===
                    "string"
                    ? data.payment
                        .redirectUrl
                    : undefined,

                createdAt:
                  new Date()
                    .toISOString(),
              };


            localStorage.setItem(
              PENDING_PAYMENT_STORAGE,
              JSON.stringify(
                handoff,
              ),
            );
          }
        } catch {}


        /*
         * LIVE SUCCESS RESPONSE:
         *
         * Не очищаем cart.
         * Не показываем orderSuccess.
         * Сначала уходим на provider.
         */
        if (
          response.ok &&
          data.mode ===
            "live"
        ) {
          setCheckoutOpen(
            false,
          );

          setCartOpen(
            false,
          );


          const redirectUrl =
            typeof data?.payment
              ?.redirectUrl ===
              "string"
              ? data.payment
                  .redirectUrl
              : "";


          if (redirectUrl) {
            window.location.assign(
              redirectUrl,
            );

            return;
          }


          alert(
            "Платёж создан. Ожидаем подтверждение оплаты.",
          );

          return;
        }
      }


      if (!response.ok) {
        throw new Error(
          data.error ||
            "Не удалось создать заказ",
        );
      }


      /*
       * TEST MODE:
       *
       * Полностью сохраняем прежнее
       * поведение v3.24.1.
       */
      const compact:
        CompactOrder = {
          id:
            data.order.id,

          customerToken:
            data.order.customerToken,

          number:
            data.order.number,

          total:
            data.order.total,

          status:
            data.order.status,

          paymentStatus:
            data.order.paymentStatus,

          pointId:
            data.order.pointId,

          pointName:
            data.order.pointName,

          createdAt:
            data.order.createdAt,

          phone:
            data.order.phone,

          paymentMethod:
            data.order.paymentMethod,

          items:
            Array.isArray(
              data.order.items,
            )
              ? data.order.items
              : [],
        };


      setCurrentOrder(
        compact,
      );


      try {
        localStorage.setItem(
          "coffeetrack:last-order",
          JSON.stringify(
            compact,
          ),
        );

        localStorage.removeItem(
          CHECKOUT_KEY_STORAGE,
        );

        // COFFEETRACK_RESUME_TEST_CLEANUP_V3261
        localStorage.removeItem(
          CHECKOUT_RESUME_STORAGE,
        );

        localStorage.removeItem(
          PENDING_PAYMENT_STORAGE,
        );
      } catch {}


      setCart([]);
      setUseLoyaltyReward(false);
      setCheckoutOpen(false);
      setCartOpen(false);
      setOrderSuccessOpen(true);

    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Ошибка заказа",
      );

    } finally {
      setPlacingOrder(
        false,
      );
    }
  };


  // COFFEETRACK_REPEAT_ORDER_V35
  const repeatOrder = (
    order: CompactOrder,
  ) => {
    const sourceItems =
      order.items ?? [];

    const nextByKey =
      new Map<
        string,
        CartLine
      >();

    let unavailableCount =
      0;

    for (
      const item
      of sourceItems
    ) {
      const product =
        item.productId
          ? products.find(
              (candidate) =>
                candidate.id ===
                item.productId,
            )
          : products.find(
              (candidate) =>
                candidate.name ===
                item.name,
            );

      if (!product) {
        unavailableCount +=
          Math.max(
            1,
            Number(
              item.qty ||
                1,
            ),
          );

        continue;
      }

      const size =
        product.sizes?.find(
          (candidate) =>
            candidate.id ===
            item.sizeId,
        ) ??
        product.sizes?.find(
          (candidate) =>
            candidate.label ===
            item.sizeLabel,
        ) ??
        product.sizes?.[0];

      const sizeId =
        size?.id ??
        "base";

      const sizeLabel =
        size?.label ??
        "";

      const storedAddonIds =
        Array.isArray(
          item.addonIds,
        )
          ? item.addonIds
          : [];

      const storedAddonLabels =
        Array.isArray(
          item.addons,
        )
          ? item.addons
          : [];

      const selectedAddons =
        (
          product.addons ??
          []
        ).filter(
          (addon) =>
            storedAddonIds.includes(
              addon.id,
            ) ||
            (
              storedAddonIds.length ===
                0 &&
              storedAddonLabels.includes(
                addon.label,
              )
            ),
        );

      const addons:
        CartAddon[] =
          selectedAddons.map(
            (addon) => ({
              id:
                addon.id,

              label:
                addon.label,

              price:
                addon.price,
            }),
          );

      const unitPrice =
        product.basePrice +
        (
          size?.delta ??
          0
        ) +
        addons.reduce(
          (
            total,
            addon,
          ) =>
            total +
            addon.price,
          0,
        );

      const qty =
        Math.max(
          1,
          Math.min(
            50,
            Math.floor(
              Number(
                item.qty,
              ) || 1,
            ),
          ),
        );

      const addonKey =
        addons
          .map(
            (addon) =>
              addon.id,
          )
          .sort()
          .join(",");

      const key =
        `${product.id}|${sizeId}|${addonKey}`;

      const existing =
        nextByKey.get(
          key,
        );

      if (existing) {
        nextByKey.set(
          key,
          {
            ...existing,

            qty:
              Math.min(
                50,
                existing.qty +
                  qty,
              ),
          },
        );

        continue;
      }

      nextByKey.set(
        key,
        {
          key,

          productId:
            product.id,

          name:
            product.name,

          image:
            product.image,

          sizeId,
          sizeLabel,

          addons,

          unitPrice,
          qty,
        },
      );
    }

    const nextCart =
      Array.from(
        nextByKey.values(),
      );

    if (!nextCart.length) {
      alert(
        "Эти позиции больше недоступны в текущем меню.",
      );

      return;
    }

    /*
     * Возвращаем старую точку,
     * только если она всё ещё активна.
     */
    // COFFEETRACK_REPEAT_POINT_OPTIONAL_NAME_V3191
    //
    // Старые заказы могут не иметь pointName.
    // Сначала используем стабильный pointId,
    // а имя оставляем только legacy fallback.
    //
    const repeatPoint =
      (
        order.pointId
          ? findActivePickupPoint(
              order.pointId,
            )
          : undefined
      ) ??
      (
        order.pointName
          ? findActivePickupPointByName(
              order.pointName,
            )
          : undefined
      );

    if (repeatPoint) {
      setPickupPoint(
        repeatPoint,
      );

      try {
        localStorage.setItem(
          "coffeetrack:pickup-point",
          repeatPoint.id,
        );
      } catch {}
    }

    /*
     * Reward прошлого заказа не переносим.
     * Если новый подарок доступен,
     * клиент выберет его заново в checkout.
     */
    setUseLoyaltyReward(
      false,
    );

    setCheckoutOpen(
      false,
    );

    setOrderSuccessOpen(
      false,
    );

    setCart(
      nextCart,
    );

    navigateTab(
      "menu",
    );

    setCartOpen(
      true,
    );

    navigator.vibrate?.(
      10,
    );

    if (
      unavailableCount > 0
    ) {
      window.setTimeout(
        () => {
          alert(
            "Часть позиций прошлого заказа больше недоступна и не была добавлена.",
          );
        },
        50,
      );
    }
  };

  const saveProfilePhone = () => {
    const digits =
      normalizePhoneDigits(
        customerPhone,
      );

    if (
      digits.length !== 11 ||
      !digits.startsWith("7")
    ) {
      setProfilePhoneMessage(
        "Введите номер в формате +7 999 123-45-67",
      );
      return;
    }

    const normalized =
      `+${digits}`;

    try {
      localStorage.setItem(
        "coffeetrack:customer-phone",
        normalized,
      );
    } catch {}

    setCustomerPhone(
      formatPhone(normalized),
    );

    setProfilePhoneMessage(
      "Номер сохранён",
    );
  };

  const statusLabel =
    currentOrder?.status === "paid"
      ? "Ждёт принятия"
      : currentOrder?.status === "accepted"
        ? "Готовится"
        : currentOrder?.status === "ready"
          ? "Можно забирать"
          : currentOrder?.status === "completed"
            ? "Выдан"
            : currentOrder?.status === "cancelled"
              ? "Отменён"
              : "Нет заказа";

  const statusTitle =
    currentOrder?.status === "ready"
      ? "Заказ готов!"
      : currentOrder?.status === "accepted"
        ? "Бариста готовит заказ"
        : currentOrder?.status === "completed"
          ? "Спасибо!"
          : "Заказ передан на точку";

  // COFFEETRACK_ORDER_VIEW_HELPERS_V22
  const currentOrderStep =
    currentOrder?.status === "paid"
      ? 0
      : currentOrder?.status === "accepted"
        ? 1
        : currentOrder?.status === "ready"
          ? 2
          : currentOrder?.status === "completed"
            ? 3
            : -1;

  const currentOrderPaymentLabel =
    currentOrder?.paymentMethod === "sbp"
      ? "СБП"
      : currentOrder?.paymentMethod === "sberpay"
        ? "SberPay"
        : currentOrder?.paymentMethod === "card"
          ? "Банковская карта"
          : "Не указана";

  const currentOrderRewardDiscount =
    currentOrder?.items?.reduce(
      (
        total,
        item,
      ) =>
        total +
        Number(
          item.rewardBaseDiscount ??
            0,
        ),
      0,
    ) ?? 0;

  // COFFEETRACK_PREVIOUS_ORDERS_V34
  const previousOrders =
    orderHistory.filter(
      (order) =>
        order.id !==
        currentOrder?.id,
    );


  return (
    <main className="min-h-screen bg-[#ebe7e1] text-[#211914]">
      <div
          className="relative mx-auto min-h-screen max-w-[1240px] overflow-hidden bg-[#faf8f5] shadow-[0_0_45px_rgba(30,20,14,.08)]" data-coffeetrack="COFFEETRACK_UNIFIED_WIDTH_V311"
        >

        {/* COFFEETRACK_DESKTOP_SHELL_V311 */}
        {tab !== "menu" ? (
          <header className="sticky top-0 z-[55] hidden border-b border-[#e9e3dc] bg-[#faf8f5]/95 px-8 py-5 backdrop-blur-xl md:block">
            <div className="flex items-center justify-between gap-6">
              <button
                type="button"
                onClick={() =>
                  navigateTab("menu")
                }
                className="flex items-center gap-2.5 text-left"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-[#f7b733] text-sm font-black text-[#211914] shadow-[0_4px_14px_rgba(247,183,51,.2)]">
                  CT
                </span>

                <span>
                  <span className="block text-[9px] font-bold uppercase tracking-[0.2em] text-[#9a8e85]">
                    CoffeeTrack
                  </span>

                  <span className="block text-[17px] font-black leading-tight">
                    Закажи. Забери.
                  </span>
                </span>
              </button>

              <div className="flex items-center gap-2">
                <nav className="flex items-center gap-1 rounded-[16px] bg-[#f0ece7] p-1">
                  {[
                    {
                      id: "menu" as AppTab,
                      label: "Меню",
                    },
                    {
                      id: "points" as AppTab,
                      label: "Точки",
                    },
                    {
                      id: "orders" as AppTab,
                      label: "Заказы",
                    },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() =>
                        navigateTab(
                          item.id,
                        )
                      }
                      className={[
                        "rounded-[12px] px-4 py-2.5 text-[12px] font-black transition",
                        tab === item.id
                          ? "bg-[#f7b733] text-[#211914] shadow-sm"
                          : "text-[#746961] hover:bg-white hover:text-[#211914]",
                      ].join(" ")}
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>

                <button
                  type="button"
                  onClick={() => {
                    if (
                      tab !==
                      "bonuses"
                    ) {
                      openProfile();
                    }
                  }}
                  className={[
                    "flex h-10 w-10 items-center justify-center rounded-full border text-[#211914] shadow-sm transition",
                    tab === "bonuses"
                      ? "border-[#f7b733] bg-[#f7b733]"
                      : "border-[#e3dcd5] bg-white hover:bg-[#f7f3ee]",
                  ].join(" ")}
                  aria-label="Профиль"
                  aria-current={
                    tab ===
                    "bonuses"
                      ? "page"
                      : undefined
                  }
                >
                  <UserRound
                    size={19}
                  />
                </button>
              </div>
            </div>
          </header>
        ) : null}

        {tab === "menu" ? (
          <>
            <header className="border-b border-[#e9e3dc] bg-[#faf8f5] px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] text-[#211914] md:px-8 md:pb-6 md:pt-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-[#f7b733] text-sm font-black text-[#211914] shadow-[0_4px_14px_rgba(247,183,51,.2)]">
                      CT
                    </span>

                    <div>
                      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#9a8e85]">
                        CoffeeTrack
                      </div>

                      <div className="text-[17px] font-black leading-tight">
                        Закажи. Забери.
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* COFFEETRACK_DESKTOP_NAV_LIGHT_V262 */}
                    <nav className="hidden items-center gap-1 rounded-[16px] bg-[#f0ece7] p-1 md:flex">
                      {[
                        {
                          id: "menu" as AppTab,
                          label: "Меню",
                        },
                        {
                          id: "points" as AppTab,
                          label: "Точки",
                        },
                        {
                          id: "orders" as AppTab,
                          label: "Заказы",
                        },
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() =>
                            navigateTab(item.id)
                          }
                          className={[
                            "rounded-[12px] px-4 py-2.5 text-[12px] font-black transition",
                            tab === item.id
                              ? "bg-[#f7b733] text-[#211914] shadow-sm"
                              : "text-[#746961] hover:bg-white hover:text-[#211914]",
                          ].join(" ")}
                        >
                          {item.label}
                        </button>
                      ))}
                    </nav>

                    {/* COFFEETRACK_MOBILE_TOP_PROFILE_DEDUPE_V315 */}
                    <button
                      type="button"
                      onClick={openProfile}
                      className="hidden h-10 w-10 items-center justify-center rounded-full border border-[#e3dcd5] bg-white text-[#211914] shadow-sm transition hover:bg-[#f7f3ee] md:flex"
                      aria-label="Профиль"
                    >
                      <UserRound size={19} />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    navigateTab("points")
                  }
                  className="mt-4 flex w-full items-center gap-3 rounded-[18px] border border-[#e7dfd8] bg-white px-3.5 py-3 text-left text-[#211914] shadow-[0_4px_18px_rgba(40,28,20,.04)] md:max-w-[520px] md:px-4"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#fff1c3]">
                    <MapPin size={19} />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block text-[10px] font-semibold text-[#8e8279]">
                      Забрать заказ
                    </span>

                    <span className="block truncate text-[13px] font-black">
                      {pickupPoint.shortName}
                    </span>
                  </span>

                  <span className="text-[11px] font-black">
                    {pickupPoint.eta}
                  </span>

                  <ChevronRight size={17} />
                </button>
              </header>

            <section className="px-4 pt-4 md:px-8 md:pt-6">
              <div className="relative overflow-hidden rounded-[24px] bg-[#f7b733] px-5 py-5 md:rounded-[30px] md:px-8 md:py-8">
                <div className="relative z-10 max-w-[76%]">
                  <div className="flex items-center gap-1.5 text-[11px] font-black">
                    <Clock3 size={14} />
                    Без очереди
                  </div>

                  <h1 className="mt-2 text-[25px] font-black leading-[1.02] tracking-[-0.045em] md:max-w-[620px] md:text-[38px]">
                    Закажи сейчас. Забери без очереди.
                  </h1>

                  <p className="mt-2 text-[12px] font-medium leading-4 text-[#604820]">
                    Кофе и еда будут готовы к твоему приходу.
                  </p>

                  {/* COFFEETRACK_HERO_MENU_CTA_V39 */}
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(
                          "coffeetrack-catalog",
                        )
                        ?.scrollIntoView({
                          behavior:
                            "smooth",
                          block:
                            "start",
                        })
                    }
                    className="mt-4 inline-flex h-11 items-center gap-2 rounded-[15px] bg-white px-4 text-[11px] font-black text-[#211914] shadow-[0_5px_18px_rgba(88,60,20,.10)] transition active:scale-[.97]"
                  >
                    <span>
                      К меню
                    </span>

                    <ChevronRight
                      size={15}
                    />
                  </button>
                </div>

                <Coffee
                  className="absolute -bottom-7 -right-4 rotate-[-10deg] opacity-[.16]"
                  size={120}
                />
              </div>
            </section>

            {/* COFFEETRACK_HOME_FOR_YOU_V36 */}
            <section className="px-4 pt-5 md:px-8 md:pt-7">
              <div className="mb-3 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#9a8e85]">
                    CoffeeTrack
                  </div>

                  <h2 className="mt-1 text-[20px] font-black tracking-[-.03em] md:text-[23px]">
                    Для вас
                  </h2>
                </div>

                <span className="text-[9px] font-bold text-[#9a8e85]">
                  Быстрые действия
                </span>
              </div>

              <div className="scrollbar-none -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0">
                {currentOrder &&
                (
                  currentOrder.status === "paid" ||
                  currentOrder.status === "accepted" ||
                  currentOrder.status === "ready"
                ) ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigateTab("orders")
                    }
                    className="min-w-[260px] snap-start rounded-[24px] bg-[#fff3c7] p-4 text-left shadow-[0_7px_24px_rgba(50,37,28,.05)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-[#f7b733]">
                        <ReceiptText
                          size={19}
                        />
                      </span>

                      <span className="rounded-full bg-white/70 px-2.5 py-1.5 text-[8px] font-black text-[#765b21]">
                        Сейчас
                      </span>
                    </div>

                    <div className="mt-5 text-[9px] font-bold text-[#8b743d]">
                      Текущий заказ
                    </div>

                    <div className="mt-1 text-[20px] font-black tracking-[-.04em]">
                      {currentOrder.number}
                    </div>

                    <div className="mt-1 text-[10px] font-bold text-[#725f35]">
                      {statusLabel}
                    </div>

                    <div className="mt-5 flex items-center justify-between text-[11px] font-black">
                      <span>
                        Открыть заказ
                      </span>

                      <ChevronRight
                        size={16}
                      />
                    </div>
                  </button>
                ) : currentOrder ? (
                  <button
                    type="button"
                    onClick={() =>
                      repeatOrder(
                        currentOrder,
                      )
                    }
                    className="min-w-[260px] snap-start rounded-[24px] bg-white p-4 text-left shadow-[0_7px_24px_rgba(50,37,28,.06)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-[#fff0bd]">
                        <ReceiptText
                          size={19}
                        />
                      </span>

                      <span className="rounded-full bg-[#f7f3ee] px-2.5 py-1.5 text-[8px] font-black text-[#81756e]">
                        1 касание
                      </span>
                    </div>

                    <div className="mt-5 text-[9px] font-bold text-[#978b83]">
                      Было вкусно?
                    </div>

                    <div className="mt-1 text-[17px] font-black leading-5">
                      Повторить последний заказ
                    </div>

                    <div className="mt-1 text-[10px] text-[#8b7f77]">
                      {currentOrder.number}
                      {" · "}
                      {money(
                        currentOrder.total,
                      )}
                    </div>

                    <div className="mt-5 flex items-center justify-between text-[11px] font-black">
                      <span>
                        В корзину
                      </span>

                      <ChevronRight
                        size={16}
                      />
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(
                          "coffeetrack-catalog",
                        )
                        ?.scrollIntoView({
                          behavior:
                            "smooth",
                          block:
                            "start",
                        })
                    }
                    className="min-w-[260px] snap-start rounded-[24px] bg-white p-4 text-left shadow-[0_7px_24px_rgba(50,37,28,.06)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-[#fff0bd]">
                      <Coffee
                        size={20}
                      />
                    </span>

                    <div className="mt-5 text-[9px] font-bold text-[#978b83]">
                      Быстрый заказ
                    </div>

                    <div className="mt-1 text-[17px] font-black leading-5">
                      Что будем сегодня?
                    </div>

                    <div className="mt-1 text-[10px] leading-4 text-[#8b7f77]">
                      Напитки и еда уже ниже.
                    </div>

                    <div className="mt-5 flex items-center justify-between text-[11px] font-black">
                      <span>
                        Смотреть меню
                      </span>

                      <ChevronRight
                        size={16}
                      />
                    </div>
                  </button>
                )}

                <button
                  type="button"
                  onClick={openProfile}
                  className="min-w-[260px] snap-start rounded-[24px] bg-white p-4 text-left shadow-[0_7px_24px_rgba(50,37,28,.06)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-[#f7b733]">
                      <Gift
                        size={19}
                      />
                    </span>

                    {loyalty?.rewardsAvailable ? (
                      <span className="rounded-full bg-[#e6f4e8] px-2.5 py-1.5 text-[8px] font-black text-[#26763d]">
                        Подарок готов
                      </span>
                    ) : (
                      <span className="rounded-full bg-[#f7f3ee] px-2.5 py-1.5 text-[8px] font-black text-[#81756e]">
                        Club
                      </span>
                    )}
                  </div>

                  <div className="mt-5 text-[9px] font-bold text-[#978b83]">
                    CoffeeTrack Club
                  </div>

                  <div className="mt-1 text-[17px] font-black leading-5">
                    6-й кофе — подарок
                  </div>

                  <div className="mt-1 text-[10px] leading-4 text-[#8b7f77]">
                    {loyalty?.rewardsAvailable
                      ? `Доступно подарков: ${loyalty.rewardsAvailable}`
                      : loyalty
                        ? `${loyalty.progress} из 5 кофе до следующего подарка`
                        : "Покупайте кофе — прогресс сохраняется по номеру."}
                  </div>

                  <div className="mt-5 flex items-center justify-between text-[11px] font-black">
                    <span>
                      Открыть профиль
                    </span>

                    <ChevronRight
                      size={16}
                    />
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigateTab("points")
                  }
                  className="min-w-[260px] snap-start rounded-[24px] bg-[#f4f0eb] p-4 text-left shadow-[0_7px_24px_rgba(50,37,28,.04)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-white">
                    <MapPin
                      size={19}
                    />
                  </span>

                  <div className="mt-5 text-[9px] font-bold text-[#978b83]">
                    Забрать заказ
                  </div>

                  <div className="mt-1 line-clamp-2 text-[17px] font-black leading-5">
                    {pickupPoint.shortName}
                  </div>

                  <div className="mt-1 text-[10px] text-[#8b7f77]">
                    Примерно{" "}
                    {pickupPoint.eta}
                  </div>

                  <div className="mt-5 flex items-center justify-between text-[11px] font-black">
                    <span>
                      Выбрать точку
                    </span>

                    <ChevronRight
                      size={16}
                    />
                  </div>
                </button>
              </div>
            </section>

            <div className="sticky top-0 z-30 mt-4 border-b border-[#e9e3dc] bg-[#faf8f5]/95 backdrop-blur-xl">
              <div className="scrollbar-none flex gap-2 overflow-x-auto px-4 py-3">
                {categories.map((item) => {
                  const active = category === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() =>
                        setCategory(item.id)
                      }
                      className={[
                        "shrink-0 rounded-full px-4 py-2.5 text-[12px] font-black",
                        active
                          ? "bg-[#211914] text-white"
                          : "bg-white text-[#645a53] shadow-sm",
                      ].join(" ")}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <section
              id="coffeetrack-catalog"
              className="px-4 pb-4 pt-5 md:px-8 md:pb-6 md:pt-7" data-coffeetrack="COFFEETRACK_CATALOG_GAP_FIX_V314"
            >
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#9a8e85]">
                    CoffeeTrack
                  </div>

                  <h2 className="mt-1 text-[23px] font-black tracking-[-0.035em]">
                    {
                      categories.find(
                        (item) =>
                          item.id === category,
                      )?.label
                    }
                  </h2>
                </div>

                <span className="text-[10px] font-bold text-[#9a8e85]">
                  {visibleProducts.length} поз.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:gap-6">
                {visibleProducts.map((product) => (
                  <article
                    key={product.id}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      openProduct(product)
                    }
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" ||
                        event.key === " "
                      ) {
                        event.preventDefault();
                        openProduct(product);
                      }
                    }}
                    className="flex h-full cursor-pointer touch-manipulation flex-col overflow-hidden rounded-[22px] bg-white text-left shadow-[0_7px_20px_rgba(50,37,28,.06)] transition duration-150 active:scale-[.975] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f7b733] md:hover:-translate-y-0.5 md:hover:shadow-[0_12px_30px_rgba(50,37,28,.09)]"
                  >
                    <div className="relative aspect-square overflow-hidden bg-[#f1ece6]">
                      <ProductPhoto
                        productId={product.id}
                        fallback={product.image}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />

                      {product.badge ? (
                        <span className="absolute left-2.5 top-2.5 rounded-full bg-[#f7b733] px-2.5 py-1 text-[9px] font-black">
                          {product.badge}
                        </span>
                      ) : null}

                      {/* COFFEETRACK_CARD_QTY_CONTROL_V291 */}
                    {productCartQty(product.id) > 0 ? (
                      <div
                        className="absolute bottom-3 right-3 z-20 flex h-11 items-center rounded-full border border-[#e8e0d9] bg-white p-1 shadow-[0_5px_18px_rgba(45,32,24,.14)]"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            setCart((current) => {
                              let index = -1;

                              for (
                                let i =
                                  current.length - 1;
                                i >= 0;
                                i -= 1
                              ) {
                                const line =
                                  current[i];

                                if (
                                  line.productId !==
                                  product.id
                                ) {
                                  continue;
                                }

                                if (
                                  index === -1
                                ) {
                                  index = i;
                                }

                                if (
                                  !line.addons?.length
                                ) {
                                  index = i;
                                  break;
                                }
                              }

                              if (index < 0) {
                                return current;
                              }

                              const next = [
                                ...current,
                              ];

                              const line =
                                next[index];

                              if (line.qty <= 1) {
                                next.splice(
                                  index,
                                  1,
                                );
                              } else {
                                next[index] = {
                                  ...line,
                                  qty:
                                    line.qty - 1,
                                };
                              }

                              return next;
                            });

                            navigator.vibrate?.(
                              8,
                            );
                          }}
                          className="flex h-9 w-9 touch-manipulation items-center justify-center rounded-full bg-[#f2eee9] text-[#211914] transition active:scale-90"
                          aria-label={`Убрать ${product.name} из корзины`}
                        >
                          <Minus size={16} />
                        </button>

                        <span className="min-w-8 px-1 text-center text-[13px] font-black tabular-nums text-[#211914]">
                          {productCartQty(
                            product.id,
                          )}
                        </span>

                        <button
                          type="button"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            quickAddProduct(
                              product,
                            );
                          }}
                          className="flex h-9 w-9 touch-manipulation items-center justify-center rounded-full bg-[#f7b733] text-[#211914] transition active:scale-90"
                          aria-label={`Добавить ещё ${product.name}. В корзине ${productCartQty(
                            product.id,
                          )}`}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();

                          quickAddProduct(
                            product,
                          );
                        }}
                        className="absolute bottom-3 right-3 z-20 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full border border-[#e8e0d9] bg-white text-[#211914] shadow-[0_5px_18px_rgba(45,32,24,.14)] transition active:scale-90"
                        aria-label={`Добавить ${product.name}`}
                      >
                        <Plus size={18} />
                      </button>
                    )}
                    </div>

                    <div className="flex flex-1 flex-col p-3.5 md:p-4">
                      <h3 className="line-clamp-2 min-h-[34px] text-[14px] font-black leading-[17px] md:text-[15px] md:leading-[18px]">
                        {product.name}
                      </h3>

                      <p className="mt-1 line-clamp-2 min-h-[30px] text-[10px] leading-[15px] text-[#8a7e76] md:mt-2 md:text-[11px] md:leading-[16px]">
                        {productDetails[product.id]?.description ??
                            product.description}
                      </p>

                      <div className="mt-auto pt-3 text-[14px] font-black md:pt-4 md:text-[15px]">
                        от {money(product.basePrice)}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
            {/* COFFEETRACK_OFFERS_AFTER_CATALOG_V39 */}
            {/* COFFEETRACK_HOME_OFFERS_V37 */}
            <section
              className="px-4 pb-32 pt-2 md:px-8 md:pb-16 md:pt-3"
              data-coffeetrack="COFFEETRACK_OFFERS_BOTTOM_RESERVE_V314"
            >
              <div className="mb-3 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#9a8e85]">
                    Актуально
                  </div>

                  <h2 className="mt-1 text-[20px] font-black tracking-[-.03em] md:text-[23px]">
                    Предложения
                  </h2>
                </div>

                <span className="text-[9px] font-bold text-[#9a8e85]">
                  CoffeeTrack
                </span>
              </div>

              <div className="scrollbar-none -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0">
                <button
                  type="button"
                  onClick={openProfile}
                  className="group relative min-w-[292px] snap-start overflow-hidden rounded-[26px] bg-[#f7b733] p-5 text-left text-[#211914] shadow-[0_9px_28px_rgba(50,37,28,.07)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                >
                  <Coffee
                    size={118}
                    className="absolute -bottom-8 -right-8 rotate-[-12deg] opacity-[.11]"
                  />

                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-white/70">
                        <Gift
                          size={19}
                        />
                      </span>

                      <span className="rounded-full bg-white/65 px-2.5 py-1.5 text-[8px] font-black">
                        CoffeeTrack Club
                      </span>
                    </div>

                    <div className="mt-7 max-w-[220px] text-[22px] font-black leading-[1.02] tracking-[-.045em]">
                      6-й кофе —
                      <br />
                      в подарок
                    </div>

                    <div className="mt-2 max-w-[240px] text-[10px] font-bold leading-4 text-[#6f531d]">
                      {loyalty?.rewardsAvailable
                        ? `У вас уже ${
                            loyalty.rewardsAvailable
                          } ${
                            loyalty.rewardsAvailable === 1
                              ? "подарок"
                              : "подарка"
                          }`
                        : loyalty
                          ? `${loyalty.progress} из 5 кофе уже куплено`
                          : "Покупайте кофе — прогресс сохраняется по номеру телефона."}
                    </div>

                    <div className="mt-7 flex items-center gap-1.5 text-[11px] font-black">
                      <span>
                        Мой прогресс
                      </span>

                      <ChevronRight
                        size={15}
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById(
                        "coffeetrack-catalog",
                      )
                      ?.scrollIntoView({
                        behavior:
                          "smooth",
                        block:
                          "start",
                      })
                  }
                  className="group relative min-w-[292px] snap-start overflow-hidden rounded-[26px] bg-[#f2eee9] p-5 text-left text-[#211914] shadow-[0_9px_28px_rgba(50,37,28,.05)] transition active:scale-[.985] md:min-w-0 md:hover:-translate-y-0.5"
                >
                  <ReceiptText
                    size={112}
                    className="absolute -bottom-8 -right-6 rotate-[8deg] opacity-[.07]"
                  />

                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-white">
                        <Coffee
                          size={19}
                        />
                      </span>

                      <span className="rounded-full bg-white px-2.5 py-1.5 text-[8px] font-black text-[#746961]">
                        Самовывоз
                      </span>
                    </div>

                    <div className="mt-7 max-w-[240px] text-[22px] font-black leading-[1.02] tracking-[-.045em]">
                      Закажи заранее.
                      <br />
                      Забери без очереди.
                    </div>

                    <div className="mt-2 max-w-[250px] text-[10px] font-medium leading-4 text-[#80746c]">
                      Выберите точку, соберите заказ и приходите уже за готовым кофе.
                    </div>

                    <div className="mt-7 flex items-center gap-1.5 text-[11px] font-black">
                      <span>
                        Выбрать из меню
                      </span>

                      <ChevronRight
                        size={15}
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-2 text-[9px] leading-4 text-[#a0958e]">
                Здесь будут появляться новые акции и специальные предложения CoffeeTrack.
              </div>
            </section>


          </>
        ) : null}

        {tab === "points" ? (
          <section className="min-h-screen pb-24 md:pb-8">
            <header className="flex items-center gap-3 border-b border-[#e7dfd8] bg-[#faf8f5] px-4 pb-4 pt-[max(15px,env(safe-area-inset-top))] text-[#211914] md:px-6 md:pb-5 md:pt-5">
              <button
                onClick={() => navigateTab("menu")}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e3dcd5] bg-white shadow-sm md:hidden"
              >
                <ArrowLeft size={19} />
              </button>

              <div>
                <div className="text-[9px] font-bold uppercase tracking-[.18em] text-[#9a8e85]">
                  CoffeeTrack
                </div>

                <h1 className="text-[20px] font-black">
                  Выберите точку
                </h1>
              </div>
            </header>

            <div className="p-4 md:grid md:grid-cols-[minmax(0,1.45fr)_minmax(350px,.55fr)] md:gap-6 md:p-6">
              <PickupMap
                points={activePickupPoints}
                selectedPointId={pickupPoint.id}
                onSelect={choosePoint}
                heightClass="h-[330px] md:h-[620px]"
              />

              <div className="mt-4 space-y-3 md:mt-0 md:max-h-[620px] md:overflow-y-auto md:pr-1">
                {activePickupPoints
                  .map((point) => {
                    const selected =
                      point.id === pickupPoint.id;

                    return (
                      <button
                        key={point.id}
                        onClick={() =>
                          choosePoint(point)
                        }
                        className={[
                          "w-full rounded-[22px] border-2 bg-white p-4 text-left",
                          selected
                            ? "border-[#211914]"
                            : "border-transparent",
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={[
                              "flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px]",
                              selected
                                ? "bg-[#f7b733]"
                                : "bg-[#f1ece6]",
                            ].join(" ")}
                          >
                            <MapPin size={20} />
                          </span>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-[14px] font-black">
                                  {point.shortName}
                                </h3>

                                <p className="mt-1 text-[11px] text-[#847870]">
                                  {point.address}
                                </p>
                              </div>

                              {selected ? (
                                <span className="rounded-full bg-[#f7b733] px-2.5 py-1.5 text-[9px] font-black text-[#211914]">
                                  Выбрано
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold">
                              <span className="rounded-full bg-[#f3eee8] px-2.5 py-1.5">
                                {point.hours}
                              </span>

                              <span className="rounded-full bg-[#f3eee8] px-2.5 py-1.5">
                                {point.eta}
                              </span>

                              {point.rating ? (
                                <span className="rounded-full bg-[#f3eee8] px-2.5 py-1.5">
                                  ★ {point.rating}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>

              <button
                onClick={() => navigateTab("menu")}
                className="mt-4 flex w-full items-center justify-between rounded-[18px] bg-[#f7b733] px-5 py-4 text-sm font-black"
              >
                <span>Заберу здесь</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </section>
        ) : null}

        {tab === "orders" ? (
          <section className="min-h-screen pb-28 md:pb-10">
            {/* COFFEETRACK_ORDERS_TAB_V22 */}

            <header className="border-b border-[#e7dfd8] bg-[#faf8f5] px-4 pb-5 pt-[max(16px,env(safe-area-inset-top))] text-[#211914] md:px-6 md:pt-5">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigateTab("menu")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e3dcd5] bg-white shadow-sm md:hidden"
                  aria-label="Вернуться в меню"
                >
                  <ArrowLeft size={19} />
                </button>

                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[.2em] text-[#9a8e85]">
                    CoffeeTrack
                  </div>

                  <h1 className="mt-0.5 text-[22px] font-black tracking-[-.03em]">
                    Заказы
                  </h1>
                </div>
              </div>
            </header>

            {!currentOrder ? (
              <div className="px-4 pt-8">
                <div className="flex flex-col items-center rounded-[28px] bg-white px-6 py-10 text-center shadow-[0_8px_28px_rgba(50,37,28,.05)]">
                  <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-[#f5f0ea]">
                    <ReceiptText size={28} />
                  </span>

                  {/* COFFEETRACK_SESSION_EMPTY_ORDER_COPY_V38B */}
                  <h2 className="mt-5 text-[20px] font-black">
                    {customerSession
                      ? "Сейчас активных заказов нет"
                      : "Заказов пока нет"}
                  </h2>

                  <p className="mt-2 max-w-[280px] text-[12px] leading-5 text-[#8b7f77]">
                    {customerSession
                      ? "Предыдущие заказы вашей учётной записи находятся ниже."
                      : "Выберите кофе или еду в меню. После оформления здесь появится номер заказа и его статус."}
                  </p>

                  <button
                    type="button"
                    onClick={() => navigateTab("menu")}
                    className="mt-6 w-full rounded-[18px] bg-[#f7b733] px-5 py-4 text-[13px] font-black"
                  >
                    Перейти в меню
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-4 pt-4 md:px-6 md:pt-6">
                <div
                  className={[
                    "overflow-hidden rounded-[28px] border-2 bg-white shadow-[0_10px_30px_rgba(50,37,28,.06)]",
                    currentOrder.status === "ready"
                      ? "border-[#82bc8c]"
                      : currentOrder.status === "cancelled"
                        ? "border-[#e19a93]"
                        : "border-transparent",
                  ].join(" ")}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-[.2em] text-[#9b8f87]">
                          Ваш заказ
                        </div>

                        <div className="mt-1 text-[45px] font-black leading-none tracking-[-.06em]">
                          {currentOrder.number}
                        </div>

                        {currentOrder.createdAt ? (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-[#978b83]">
                            <Clock3 size={12} />

                            {new Date(
                              currentOrder.createdAt,
                            ).toLocaleTimeString(
                              "ru-RU",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </div>
                        ) : null}
                      </div>

                      <span
                        className={[
                          "shrink-0 rounded-full px-3 py-2 text-[10px] font-black",
                          currentOrder.status === "ready"
                            ? "bg-[#e1f4e5] text-[#28743d]"
                            : currentOrder.status === "cancelled"
                              ? "bg-[#f8e2df] text-[#a43f36]"
                              : currentOrder.status === "accepted"
                                ? "bg-[#fff0bd]"
                                : currentOrder.status === "completed"
                                  ? "bg-[#ece8e3] text-[#736860]"
                                  : "bg-[#f1ece6] text-[#5f554f]",
                        ].join(" ")}
                      >
                        {statusLabel}
                      </span>
                    </div>

                    {currentOrder.status !== "cancelled" ? (
                      <div className="mt-6">
                        <div className="grid grid-cols-4 gap-1.5">
                          {[
                            "Принят",
                            "Готовится",
                            "Готов",
                            "Выдан",
                          ].map((label, index) => (
                            <div
                              key={label}
                              className="min-w-0"
                            >
                              <div
                                className={[
                                  "h-1.5 rounded-full transition-all",
                                  currentOrderStep >= index
                                    ? index === 2 &&
                                      currentOrder.status === "ready"
                                      ? "bg-[#65aa75]"
                                      : "bg-[#f7b733]"
                                    : "bg-[#eee9e4]",
                                ].join(" ")}
                              />

                              <div
                                className={[
                                  "mt-2 truncate text-center text-[8px] font-bold",
                                  currentOrderStep >= index
                                    ? "text-[#211914]"
                                    : "text-[#aaa098]",
                                ].join(" ")}
                              >
                                {label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div
                      className={[
                        "mt-6 rounded-[20px] p-4",
                        currentOrder.status === "ready"
                          ? "bg-[#e7f5e9]"
                          : currentOrder.status === "cancelled"
                            ? "bg-[#f9e8e6]"
                            : "bg-[#fff5d9]",
                      ].join(" ")}
                    >
                      <div className="text-[13px] font-black">
                        {currentOrder.status === "ready"
                          ? "Можно забирать"
                          : currentOrder.status === "accepted"
                            ? "Бариста готовит заказ"
                            : currentOrder.status === "completed"
                              ? "Заказ выдан"
                              : currentOrder.status === "cancelled"
                                ? "Заказ отменён"
                                : "Заказ передан на точку"}
                      </div>

                      <p className="mt-1 text-[10px] leading-4 text-[#756960]">
                        {currentOrder.status === "ready"
                          ? `Подойдите к CoffeeTrack и назовите номер ${currentOrder.number}.`
                          : currentOrder.status === "accepted"
                            ? "Статус обновится автоматически, когда заказ будет готов."
                            : currentOrder.status === "completed"
                              ? "Спасибо! Будем рады приготовить для вас снова."
                              : currentOrder.status === "cancelled"
                                ? "Если отмена произошла неожиданно, обратитесь к сотруднику CoffeeTrack."
                                : "Сотрудник увидит заказ и примет его в работу."}
                      </p>
                    </div>

                    {currentOrder.pointName ? (
                      <div className="mt-4 flex items-start gap-3 rounded-[18px] bg-[#f7f3ee] p-4">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-white">
                          <MapPin size={16} />
                        </span>

                        <div className="min-w-0">
                          <div className="text-[9px] font-bold text-[#988c84]">
                            Забрать в точке
                          </div>

                          <div className="mt-0.5 text-[12px] font-black leading-4">
                            {currentOrder.pointName}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="border-t border-[#eee8e2] px-5 py-5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[14px] font-black">
                        Состав заказа
                      </h3>

                      <span className="text-[10px] font-bold text-[#998d85]">
                        {currentOrder.items?.reduce(
                          (sum, item) =>
                            sum + item.qty,
                          0,
                        ) ?? 0} поз.
                      </span>
                    </div>

                    {currentOrder.items?.length ? (
                      <div className="mt-4 space-y-3">
                        {currentOrder.items.map(
                          (item, index) => (
                            <div
                              key={`${currentOrder.id}-${index}`}
                              className="flex justify-between gap-4 border-b border-[#eee9e4] pb-3 last:border-0 last:pb-0"
                            >
                              <div className="min-w-0">
                                {/* COFFEETRACK_CUSTOMER_GIFT_V33C2 */}
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-[12px] font-black">
                                    {item.qty} × {item.name}
                                  </div>

                                  {Number(
                                    item.rewardQty ??
                                      0,
                                  ) > 0 ? (
                                    <span className="rounded-full bg-[#f7b733] px-2 py-0.5 text-[8px] font-black text-[#211914]">
                                      Подарок
                                    </span>
                                  ) : null}
                                </div>

                                {item.sizeLabel ? (
                                  <div className="mt-1 text-[10px] text-[#8e827a]">
                                    {item.sizeLabel}
                                  </div>
                                ) : null}

                                {item.addons?.length ? (
                                  <div className="mt-0.5 text-[10px] leading-4 text-[#8e827a]">
                                    {item.addons.join(
                                      ", ",
                                    )}
                                  </div>
                                ) : null}
                              </div>

                              <div className="shrink-0 text-[12px] font-black">
                                {/* COFFEETRACK_CUSTOMER_GIFT_PRICE_V33C2 */}
                                {money(
                                  Math.max(
                                    0,
                                    item.unitPrice *
                                      item.qty -
                                      Number(
                                        item.rewardBaseDiscount ??
                                          0,
                                      ),
                                  ),
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-[16px] bg-[#f7f3ee] p-4 text-[10px] leading-4 text-[#8e827a]">
                        Загружаем состав заказа…
                      </div>
                    )}

                    <div className="mt-5 grid grid-cols-2 gap-2">
                      <div className="rounded-[17px] bg-[#f7f3ee] p-3">
                        <div className="text-[9px] font-bold text-[#978b83]">
                          Оплата
                        </div>

                        <div className="mt-1 text-[11px] font-black">
                          {currentOrderPaymentLabel}
                        </div>
                      </div>

                      <div className="rounded-[17px] bg-[#f7f3ee] p-3">
                        <div className="text-[9px] font-bold text-[#978b83]">
                          Телефон
                        </div>

                        <div className="mt-1 truncate text-[11px] font-black">
                          {currentOrder.phone
                            ? formatPhone(
                                currentOrder.phone,
                              )
                            : "Не указан"}
                        </div>
                      </div>
                    </div>

                    {/* COFFEETRACK_ORDER_GIFT_SUMMARY_V33C2 */}
                    {currentOrderRewardDiscount > 0 ? (
                      <div className="mt-5 flex items-center justify-between rounded-[16px] bg-[#fff4cf] px-3.5 py-3">
                        <div>
                          <div className="text-[10px] font-black text-[#765b21]">
                            Подарок CoffeeTrack
                          </div>

                          <div className="mt-0.5 text-[9px] text-[#9a7a37]">
                            Бесплатна базовая стоимость одного кофе
                          </div>
                        </div>

                        <div className="text-[12px] font-black text-[#26763d]">
                          −{money(
                            currentOrderRewardDiscount,
                          )}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-5 flex items-end justify-between border-t border-[#eee8e2] pt-5">
                      <div>
                        <div className="text-[9px] font-bold text-[#998d85]">
                          Итого
                        </div>

                        <div className="mt-0.5 text-[24px] font-black tracking-[-.04em]">
                          {money(currentOrder.total)}
                        </div>
                      </div>

                      <div className="text-right text-[9px] leading-4 text-[#9b9088]">
                        Статус обновляется
                        <br />
                        автоматически
                      </div>
                    </div>

                    {/* COFFEETRACK_CURRENT_REPEAT_BUTTON_V35 */}
                    {currentOrder.status ===
                      "completed" ||
                    currentOrder.status ===
                      "cancelled" ? (
                      <button
                        type="button"
                        onClick={() =>
                          repeatOrder(
                            currentOrder,
                          )
                        }
                        className="mt-5 flex w-full items-center justify-between rounded-[18px] bg-[#f7b733] px-5 py-4 text-[13px] font-black text-[#211914] transition hover:brightness-[.98] active:scale-[.99]"
                      >
                        <span>
                          Повторить заказ
                        </span>

                        <ChevronRight
                          size={17}
                        />
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => navigateTab("menu")}
                      className={[
                        "w-full rounded-[18px] px-5 py-4 text-[13px] font-black transition active:scale-[.99] md:hidden",
                        currentOrder.status ===
                          "completed" ||
                        currentOrder.status ===
                          "cancelled"
                          ? "mt-2 border border-[#e5ded7] bg-white text-[#211914]"
                          : "mt-5 bg-[#f7b733] text-[#211914]",
                      ].join(" ")}
                    >
                      Вернуться в меню
                    </button>
                  </div>
                </div>
              </div>
            )}
          {/* COFFEETRACK_ORDER_HISTORY_UI_V34 */}
          {/* COFFEETRACK_SESSION_HISTORY_UI_V38B */}
          {currentOrder ||
          customerSession ? (
            <div className="px-4 pb-4 pt-7 md:px-6 md:pt-8">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#9a8e85]">
                    CoffeeTrack
                  </div>

                  <h2 className="mt-1 text-[20px] font-black tracking-[-.03em]">
                    История заказов
                  </h2>
                </div>

                {!orderHistoryLoading &&
                !orderHistoryError ? (
                  <span className="rounded-full bg-white px-3 py-2 text-[10px] font-black text-[#776b63] shadow-sm">
                    {previousOrders.length}
                  </span>
                ) : null}
              </div>

              {orderHistoryLoading ? (
                <div className="mt-4 rounded-[22px] bg-white p-5 text-[11px] font-bold text-[#8b7f77] shadow-[0_7px_22px_rgba(50,37,28,.04)]">
                  Загружаем предыдущие заказы…
                </div>
              ) : orderHistoryError ? (
                <div className="mt-4 rounded-[22px] bg-[#f8e9e6] p-5">
                  <div className="text-[12px] font-black text-[#99483f]">
                    История временно недоступна
                  </div>

                  <div className="mt-1 text-[10px] leading-4 text-[#9b6a64]">
                    {orderHistoryError}
                  </div>
                </div>
              ) : previousOrders.length ? (
                <div className="mt-4 space-y-3">
                  {previousOrders.map(
                    (order) => {
                      const statusMeta =
                        orderHistoryStatusMeta(
                          order.status,
                        );

                      const rewardDiscount =
                        order.items?.reduce(
                          (
                            total,
                            item,
                          ) =>
                            total +
                            Number(
                              item.rewardBaseDiscount ??
                                0,
                            ),
                          0,
                        ) ?? 0;

                      return (
                        <details
                          key={order.id}
                          className="group overflow-hidden rounded-[22px] bg-white shadow-[0_7px_24px_rgba(50,37,28,.05)]"
                        >
                          <summary className="flex cursor-pointer list-none items-center gap-3 p-4 [&::-webkit-details-marker]:hidden">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#f7f3ee]">
                              <ReceiptText
                                size={18}
                              />
                            </span>

                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-2">
                                <span className="text-[14px] font-black">
                                  {order.number}
                                </span>

                                <span
                                  className={[
                                    "rounded-full px-2 py-1 text-[8px] font-black",
                                    statusMeta.className,
                                  ].join(
                                    " ",
                                  )}
                                >
                                  {
                                    statusMeta.label
                                  }
                                </span>
                              </span>

                              <span className="mt-1 block text-[9px] font-bold text-[#998d85]">
                                {formatOrderHistoryDate(
                                  order.createdAt,
                                )}
                              </span>
                            </span>

                            <span className="shrink-0 text-right">
                              <span className="block text-[13px] font-black">
                                {money(
                                  order.total,
                                )}
                              </span>

                              <ChevronRight
                                size={16}
                                className="ml-auto mt-1 text-[#9a8e85] transition-transform group-open:rotate-90"
                              />
                            </span>
                          </summary>

                          <div className="border-t border-[#eee8e2] px-4 pb-4 pt-4">
                            {order.pointName ? (
                              <div className="mb-4 flex items-start gap-2.5 rounded-[15px] bg-[#f7f3ee] p-3">
                                <MapPin
                                  size={15}
                                  className="mt-0.5 shrink-0"
                                />

                                <div>
                                  <div className="text-[8px] font-bold uppercase tracking-[.12em] text-[#9a8e85]">
                                    Точка
                                  </div>

                                  <div className="mt-0.5 text-[10px] font-black leading-4">
                                    {
                                      order.pointName
                                    }
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            <div className="space-y-3">
                              {order.items?.map(
                                (
                                  item,
                                  index,
                                ) => {
                                  const itemDiscount =
                                    Number(
                                      item.rewardBaseDiscount ??
                                        0,
                                    );

                                  const itemTotal =
                                    Math.max(
                                      0,
                                      item.unitPrice *
                                        item.qty -
                                        itemDiscount,
                                    );

                                  return (
                                    <div
                                      key={`${order.id}-history-${index}`}
                                      className="flex items-start justify-between gap-4 border-b border-[#eee9e4] pb-3 last:border-0 last:pb-0"
                                    >
                                      <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-[11px] font-black">
                                            {
                                              item.qty
                                            }{" "}
                                            ×{" "}
                                            {
                                              item.name
                                            }
                                          </span>

                                          {Number(
                                            item.rewardQty ??
                                              0,
                                          ) > 0 ? (
                                            <span className="rounded-full bg-[#f7b733] px-2 py-0.5 text-[8px] font-black">
                                              Подарок
                                            </span>
                                          ) : null}
                                        </div>

                                        {item.sizeLabel ? (
                                          <div className="mt-1 text-[9px] text-[#8e827a]">
                                            {
                                              item.sizeLabel
                                            }
                                          </div>
                                        ) : null}

                                        {item.addons?.length ? (
                                          <div className="mt-0.5 text-[9px] leading-4 text-[#8e827a]">
                                            {item.addons.join(
                                              ", ",
                                            )}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div className="shrink-0 text-right">
                                        <div className="text-[11px] font-black">
                                          {money(
                                            itemTotal,
                                          )}
                                        </div>

                                        {itemDiscount >
                                        0 ? (
                                          <div className="mt-0.5 text-[8px] font-bold text-[#26763d]">
                                            −{money(
                                              itemDiscount,
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>

                            {rewardDiscount > 0 ? (
                              <div className="mt-4 flex items-center justify-between rounded-[14px] bg-[#fff4cf] px-3 py-2.5">
                                <span className="text-[9px] font-black text-[#765b21]">
                                  Подарок CoffeeTrack
                                </span>

                                <span className="text-[10px] font-black text-[#26763d]">
                                  −{money(
                                    rewardDiscount,
                                  )}
                                </span>
                              </div>
                            ) : null}

                            {/* COFFEETRACK_HISTORY_REPEAT_BUTTON_V35 */}
                            {order.status ===
                              "completed" ||
                            order.status ===
                              "cancelled" ? (
                              <button
                                type="button"
                                onClick={() =>
                                  repeatOrder(
                                    order,
                                  )
                                }
                                className="mt-4 flex w-full items-center justify-between rounded-[15px] bg-[#f7b733] px-4 py-3.5 text-[11px] font-black text-[#211914] transition active:scale-[.99]"
                              >
                                <span>
                                  Повторить заказ
                                </span>

                                <ChevronRight
                                  size={15}
                                />
                              </button>
                            ) : null}
                          </div>
                        </details>
                      );
                    },
                  )}
                </div>
              ) : (
                <div className="mt-4 rounded-[22px] bg-white p-5 shadow-[0_7px_22px_rgba(50,37,28,.04)]">
                  <div className="text-[12px] font-black">
                    Предыдущих заказов пока нет
                  </div>

                  <div className="mt-1 text-[10px] leading-4 text-[#8b7f77]">
                    После следующего заказа предыдущий появится здесь.
                  </div>
                </div>
              )}
            </div>
          ) : null}

          </section>
        ) : null}

        {tab === "bonuses" ? (
          <section className="min-h-screen pb-24">
            <header className="bg-[#faf8f5] px-4 pb-5 pt-[max(18px,env(safe-area-inset-top))] text-[#211914] border-b border-[#e7dfd8] md:px-6 md:pt-5">
              <div className="text-[9px] font-bold uppercase tracking-[.18em] text-[#9a8e85]">
                CoffeeTrack
              </div>

              {/* COFFEETRACK_PROFILE_TITLE_V312 */}
<h1 className="mt-1 text-[23px] font-black">
                Профиль
              </h1>
            </header>

            <div className="p-4 md:px-6 md:pt-6 md:pb-10">
              {/* COFFEETRACK_PROFILE_INTRO_COMPACT_V313 */}
              <div className="relative overflow-hidden rounded-[24px] bg-[#f7b733] p-5 md:flex md:items-center md:justify-between md:gap-6 md:px-6 md:py-4">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] bg-white/70">
                    <Gift size={21} />
                  </span>

                  <div>
                    <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#7d5e20]">
                      CoffeeTrack
                    </div>

                    <h2 className="mt-1 text-[20px] font-black leading-tight tracking-[-.035em] md:text-[21px]">
                      Ваш профиль
                    </h2>

                    <p className="mt-1 text-[11px] leading-4 text-[#604820]">
                      Заказы, CoffeeTrack Club и персональные предложения — в одном месте.
                    </p>
                  </div>
                </div>

                <span className="mt-4 inline-flex rounded-full bg-white/65 px-3 py-2 text-[9px] font-black text-[#674b15] md:mt-0 md:shrink-0">
                  Всё сохраняется по номеру
                </span>
              </div>

              {/* COFFEETRACK_PROFILE_PLAN_REMOVED_V313 */}
                {/* COFFEETRACK_PROFILE_CARD_V24 */}
                {/* COFFEETRACK_PROFILE_GRID_V29 */}
<div className="contents md:grid md:grid-cols-2 md:items-start md:gap-5">
<div className="mt-5 rounded-[24px] bg-[#f5f0ea] p-4 md:mt-0 md:h-full">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#9a8e85]">
                        Профиль
                      </div>

                      <div className="mt-1 text-[16px] font-black">
                        Номер для заказов
                      </div>
                    </div>

                    {/* COFFEETRACK_PROFILE_SESSION_BADGE_V38A */}
                    <span
                      className={[
                        "rounded-full px-3 py-1.5 text-[9px] font-black",
                        customerSession
                          ? "bg-[#e4f4e7] text-[#28743d]"
                          : "bg-white text-[#7f746d]",
                      ].join(" ")}
                    >
                      {customerSessionLoading
                        ? "Проверяем"
                        : customerSession
                          ? "Аккаунт"
                          : "Гость"}
                    </span>
                  </div>

                  <p className="mt-2 text-[10px] leading-4 text-[#8b7f77]">
                    По этому номеру CoffeeTrack связывает
                    ваши заказы и программу лояльности.
                  </p>

                  <label
                    htmlFor="profile-phone"
                    className="mt-4 block text-[10px] font-black text-[#72675f]"
                  >
                    Телефон
                  </label>

                  <input
                    data-coffeetrack="COFFEETRACK_PROFILE_LOCKED_PHONE_V312"
                    readOnly={Boolean(
                      customerSession?.id,
                    )}
                    id="profile-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+7 999 123-45-67"
                    value={customerPhone}
                    onChange={(event) => {
                      setCustomerPhone(
                        formatPhone(
                          event.target.value,
                        ),
                      );

                      setProfilePhoneMessage("");
                    }}
                    className="mt-2 w-full rounded-[16px] border border-[#ded6cf] bg-white px-4 py-3.5 text-[14px] font-black outline-none transition focus:border-[#211914]"
                  />

                  {/* COFFEETRACK_PROFILE_PHONE_STATUS_V313 */}
                  {customerSession ? (
                    <div className="mt-3 flex items-center gap-2 rounded-[14px] bg-[#e4f4e7] px-3.5 py-3 text-[10px] font-black text-[#28743d]">
                      <Check
                        size={14}
                        strokeWidth={2.8}
                      />

                      <span>
                        Номер привязан к профилю
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={saveProfilePhone}
                      className="mt-3 w-full rounded-[16px] bg-[#f7b733] px-5 py-3.5 text-[12px] font-black text-[#211914] transition hover:brightness-[.98] active:scale-[.98]"
                    >
                      Сохранить номер
                    </button>
                  )}

                  {profilePhoneMessage ? (
                    <div
                      className={[
                        "mt-3 rounded-[14px] px-3 py-2.5 text-[10px] font-bold",
                        profilePhoneMessage ===
                        "Номер сохранён"
                          ? "bg-[#e4f4e7] text-[#28743d]"
                          : "bg-[#f8e5e2] text-[#a44238]",
                      ].join(" ")}
                    >
                      {profilePhoneMessage}
                    </div>
                  ) : null}
                </div>

                <div className="mt-4 rounded-[24px] border border-[#eadfce] bg-[#fffaf0] p-5 text-[#211914] shadow-[0_8px_24px_rgba(75,55,38,.05)] md:mt-0 md:h-full">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#8c7650]">
                        CoffeeTrack Club
                      </div>

                      <div className="mt-1 text-[18px] font-black">
                        6-й кофе в подарок
                      </div>
                    </div>

                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[15px] bg-[#f7b733] text-[#211914]">
                      <Gift size={20} />
                    </span>
                  </div>

                  <div className="mt-5 grid grid-cols-5 gap-2">
                    {[0, 1, 2, 3, 4].map(
                      (index) => {
                        const active =
                          (loyalty?.progress ?? 0) >
                          index;

                        return (
                          <div
                            key={index}
                            className={[
                              "flex h-9 items-center justify-center rounded-[12px] text-[11px] font-black",
                              active
                                ? "bg-[#f7b733] text-[#211914]"
                                : "bg-[#efe5d5] text-[#8c7650]",
                            ].join(" ")}
                          >
                            {active
                              ? "✓"
                              : index + 1}
                          </div>
                        );
                      },
                    )}
                  </div>

                  <div className="mt-4 flex items-end justify-between gap-4">
                    <div>
                      <div className="text-[10px] text-[#8c7650]">
                        Прогресс
                      </div>

                      <div className="mt-0.5 text-[14px] font-black">
                        {loyaltyLoading
                          ? "Обновляем…"
                          : loyalty?.rewardsAvailable
                            ? `Подарков доступно: ${loyalty.rewardsAvailable}`
                            : `${loyalty?.progress ?? 0} из 5`}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-[10px] text-[#8c7650]">
                        Выдано заказов
                      </div>

                      <div className="mt-0.5 text-[14px] font-black">
                        {loyalty?.completedOrders ?? 0}
                      </div>
                    </div>
                  </div>

                  {!loyalty?.rewardsAvailable &&
                  loyalty?.remaining ? (
                    <p className="mt-4 text-[10px] leading-4 text-[#8c7650]">
                      Ещё {loyalty.remaining} кофе до подарка.
                    </p>
                  ) : null}
                </div>

                {/* COFFEETRACK_PROFILE_SESSION_CARD_V38A */}
                {/* COFFEETRACK_PROFILE_DEVICE_COMPACT_V313 */}
                <div className="mt-4 rounded-[18px] border border-[#e5ded7] bg-white p-4 md:col-span-2 md:mt-0 md:px-5 md:py-3.5">
                  {customerSession ? (
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-[11px] font-black">
                          Доступ к заказам сохранён
                        </div>

                        <p className="mt-1 text-[9px] leading-4 text-[#8d8179]">
                          На этом устройстве история заказов и CoffeeTrack Club откроются автоматически.
                        </p>
                      </div>

                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-[#e4f4e7] text-[#28743d]">
                        <Check
                          size={16}
                          strokeWidth={2.6}
                        />
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-[11px] font-black">
                          Вход на другом устройстве
                        </div>

                        <p className="mt-1 text-[9px] leading-4 text-[#8d8179]">
                          Заказывать можно без регистрации. Подтверждение номера для входа на другом устройстве добавим позже.
                        </p>
                      </div>

                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-[#f5f0ea] text-[#786d65]">
                        <UserRound
                          size={16}
                        />
                      </span>
                    </div>
                  )}
                </div>


              </div>
            </div>
          </section>
        ) : null}

        {cartCount > 0 && tab === "menu" ? (
          <div className="fixed bottom-[72px] left-0 right-0 z-40 px-3 md:bottom-6 md:left-auto md:right-6 md:w-[380px] md:px-0">
            <div className="mx-auto max-w-[496px]">
              <button
                onClick={() => setCartOpen(true)}
                className="flex w-full items-center justify-between rounded-[20px] bg-[#f7b733] px-4 py-3.5 shadow-[0_12px_35px_rgba(55,38,10,.2)]"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-9 min-w-9 items-center justify-center rounded-full border border-[#e8d294] bg-white px-2 text-[13px] font-black text-[#211914] shadow-sm">
                    {cartCount}
                  </span>

                  <span className="text-left">
                    <span className="block text-[10px] font-bold opacity-60">
                      Корзина
                    </span>

                    <span className="block text-[13px] font-black">
                      {money(cartTotal)}
                    </span>
                  </span>
                </span>

                <span className="text-[12px] font-black">
                  Открыть →
                </span>
              </button>
            </div>
          </div>
        ) : null}

        <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#e5dfd8] bg-white/96 pb-[max(7px,env(safe-area-inset-bottom))] backdrop-blur-xl md:hidden">
          <div className="mx-auto grid max-w-[520px] grid-cols-4">
            {[
              {
                id: "menu" as const,
                label: "Меню",
                icon: Home,
              },
              {
                id: "points" as const,
                label: "Точки",
                icon: MapPin,
              },
              {
                id: "orders" as const,
                label: "Заказы",
                icon: ReceiptText,
              },
              {
                // COFFEETRACK_MOBILE_PROFILE_NAV_V315
                id: "bonuses" as const,
                label: "Профиль",
                icon: UserRound,
              },
            ].map((item) => {
              const active = tab === item.id;
              const Icon = item.icon;

              return (
                <button
                  key={item.id}
                  onClick={() => navigateTab(item.id)}
                  className={[
                    "flex flex-col items-center gap-1 py-2",
                    active
                      ? "text-[#211914]"
                      : "text-[#9a8e86]",
                  ].join(" ")}
                >
                  <Icon
                    size={20}
                    strokeWidth={active ? 2.5 : 2}
                  />

                  <span className="text-[9px] font-black">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {selectedProduct ? (
          <div className="fixed inset-0 z-[80] flex items-end justify-center bg-[#5b514a]/35 backdrop-blur-[2px] md:items-center md:p-6">
            <div
              data-coffeetrack="COFFEETRACK_PRODUCT_CLOSE_CORNER_V317"
              className="relative max-h-[94dvh] w-full max-w-[520px] overflow-y-auto rounded-t-[30px] bg-[#faf8f5] md:grid md:h-[760px] md:max-h-[90vh] md:max-w-[960px] md:grid-cols-[420px_minmax(0,1fr)] md:overflow-hidden md:rounded-[30px] md:shadow-[0_28px_90px_rgba(60,45,35,.22)]"
            >
              <div className="aspect-[1.12] overflow-hidden bg-[#f3efe9] md:aspect-auto md:h-full md:min-h-0">
                {/* COFFEETRACK_PRODUCT_MODAL_IMAGE_PRIORITY_V318 */}
                <ProductPhoto
                  productId={selectedProduct.id}
                  fallback={selectedProduct.image}
                  alt={selectedProduct.name}
                  loading="eager"
                  fetchPriority="high"
                  className="h-full w-full object-cover"
                />

                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute right-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-[0_4px_18px_rgba(35,25,20,.16)] transition hover:bg-[#f7f3ee] active:scale-[.96] md:right-5 md:top-5"
                  aria-label="Закрыть"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-5 pb-[max(24px,env(safe-area-inset-bottom))] pt-5 md:min-h-0 md:overflow-y-auto md:px-7 md:pb-7 md:pt-7">
                <div
                  className="flex items-start justify-between gap-4 md:pr-14"
                  data-coffeetrack="COFFEETRACK_PRODUCT_CLOSE_SPACE_V3172"
                >
                  <div>
                    <h2 className="text-[25px] font-black tracking-[-.04em]">
                      {selectedProduct.name}
                    </h2>

                    <p className="mt-1 text-[12px] leading-5 text-[#857970]">
                      {selectedProductDetails?.description ?? selectedProduct.description}
                    </p>
                  </div>

                  <div className="shrink-0 text-[17px] font-black">
                    {money(configuredUnitPrice)}
                  </div>
                </div>

                {selectedProduct.sizes?.length ? (
                  <div className="mt-6">
                    <h3 className="text-[13px] font-black">
                      Размер
                    </h3>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {selectedProduct.sizes.map((size) => {
                        const active =
                          selectedSizeId === size.id;

                        return (
                          <button
                            key={size.id}
                            onClick={() =>
                              setSelectedSizeId(size.id)
                            }
                            className={[
                              "rounded-[16px] border px-4 py-3 text-left",
                              active
                                ? "border-[#f7b733] bg-[#f7b733] text-[#211914]"
                                : "border-[#ded6cf] bg-white",
                            ].join(" ")}
                          >
                            <div className="text-[12px] font-black">
                              {size.label}
                            </div>

                            <div
                              className={[
                                "mt-1 text-[10px]",
                                active
                                  ? "text-[#725515]"
                                  : "text-[#958981]",
                              ].join(" ")}
                            >
                              {size.delta > 0
                                ? `+ ${money(size.delta)}`
                                : money(
                                    selectedProduct.basePrice,
                                  )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {selectedProduct.addons?.length ? (
                  <div className="mt-6">
                    <h3 className="text-[13px] font-black">
                      Добавить
                    </h3>

                    <div className="mt-2 overflow-hidden rounded-[18px] bg-white">
                      {selectedProduct.addons.map(
                        (addon, index) => {
                          const active =
                            selectedAddonIds.includes(
                              addon.id,
                            );

                          return (
                            <button
                              key={addon.id}
                              onClick={() =>
                                toggleAddon(addon.id)
                              }
                              className={[
                                "flex w-full items-center gap-3 px-4 py-3.5 text-left",
                                index
                                  ? "border-t border-[#eee8e2]"
                                  : "",
                              ].join(" ")}
                            >
                              <span
                                className={[
                                  "flex h-6 w-6 items-center justify-center rounded-lg border",
                                  active
                                    ? "border-[#f7b733] bg-[#f7b733] text-[#211914]"
                                    : "border-[#d8d0c8]",
                                ].join(" ")}
                              >
                                {active ? (
                                  <Check size={14} />
                                ) : null}
                              </span>

                              <span className="flex-1 text-[12px] font-bold">
                                {addon.label}
                              </span>

                              <span className="text-[12px] font-black">
                                +{money(addon.price)}
                              </span>
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                ) : null}

                <button
                  onClick={() => setProductInfoOpen(true)}
                  className="mt-5 flex w-full items-center rounded-[20px] bg-white p-4 text-left"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-[#f3eee8]">
                    <Info size={18} />
                  </span>

                  <div className="ml-3 min-w-0 flex-1">
                    <div className="text-[12px] font-black">
                      О продукте
                    </div>

                    <div className="mt-1 text-[10px] leading-4 text-[#8a7e76]">
                      Состав, аллергены и пищевая ценность
                    </div>
                  </div>

                  <ChevronRight
                    className="shrink-0 text-[#a3978f]"
                    size={17}
                  />
                </button>

                {/* COFFEETRACK_PRODUCT_STICKY_ACTION_V39 */}
                <div className="sticky bottom-0 z-20 -mx-5 mt-5 flex items-center gap-3 border-t border-[#e8e1da] bg-[#faf8f5]/96 px-5 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl md:-mx-7 md:px-7 md:pb-0">
                  <div className="flex items-center rounded-[18px] bg-white p-1 shadow-sm">
                    <button
                      onClick={() =>
                        setProductQty((value) =>
                          Math.max(1, value - 1),
                        )
                      }
                      className="flex h-11 w-11 items-center justify-center"
                    >
                      <Minus size={17} />
                    </button>

                    <span className="w-8 text-center text-[13px] font-black">
                      {productQty}
                    </span>

                    <button
                      onClick={() =>
                        setProductQty(
                          (value) => value + 1,
                        )
                      }
                      className="flex h-11 w-11 items-center justify-center"
                    >
                      <Plus size={17} />
                    </button>
                  </div>

                  <button
                    onClick={addProduct}
                    className="flex h-[52px] flex-1 items-center justify-between rounded-[18px] bg-[#f7b733] px-5 text-[13px] font-black text-[#211914] transition hover:brightness-[.98] active:scale-[.99]"
                  >
                    <span>Добавить</span>

                    <span>
                      {money(
                        configuredUnitPrice *
                          productQty,
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {productInfoOpen && selectedProduct ? (
          <div className="fixed inset-0 z-[95] flex items-end justify-center bg-[#5b514a]/35 backdrop-blur-[2px] md:items-center md:p-6">
            <div className="flex max-h-[92dvh] w-full max-w-[520px] flex-col overflow-hidden rounded-t-[30px] bg-[#faf8f5] md:max-h-[86vh] md:max-w-[680px] md:rounded-[30px] md:shadow-[0_28px_90px_rgba(60,45,35,.22)]">

              <div className="shrink-0 px-5 pb-4 pt-3">
                <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#d8d1ca]" />

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-[.18em] text-[#958980]">
                      CoffeeTrack
                    </div>

                    <h2 className="mt-1 text-[22px] font-black tracking-[-.035em]">
                      О продукте
                    </h2>
                  </div>

                  <button
                    onClick={() => setProductInfoOpen(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm"
                  >
                    <X size={19} />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(28px,env(safe-area-inset-bottom))]">

                <div className="rounded-[24px] bg-white p-5">
                  <h3 className="text-[19px] font-black">
                    {selectedProduct.name}
                  </h3>

                  <p className="mt-2 text-[12px] leading-5 text-[#81756e]">
                    {selectedProductDetails?.description ?? selectedProduct.description}
                  </p>
                </div>

                {selectedNutrition ? (
                  <div className="mt-3 rounded-[24px] bg-white p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[15px] font-black">
                        Пищевая ценность
                      </h3>

                      <span className="rounded-full bg-[#fff1c3] px-2.5 py-1.5 text-[9px] font-black">
                        ориентировочно
                      </span>
                    </div>

                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] text-[#968a82]">
                          Порция
                        </div>

                        <div className="mt-1 text-[16px] font-black">
                          {selectedNutrition.serving}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-[10px] text-[#968a82]">
                          Энергетическая ценность
                        </div>

                        <div className="mt-1 text-[20px] font-black">
                          {selectedNutrition.calories} ккал
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-2">
                      <div className="rounded-[16px] bg-[#f5f1ec] p-3">
                        <div className="text-[9px] text-[#93877f]">
                          Белки
                        </div>

                        <div className="mt-1 text-[15px] font-black">
                          {selectedNutrition.protein} г
                        </div>
                      </div>

                      <div className="rounded-[16px] bg-[#f5f1ec] p-3">
                        <div className="text-[9px] text-[#93877f]">
                          Жиры
                        </div>

                        <div className="mt-1 text-[15px] font-black">
                          {selectedNutrition.fat} г
                        </div>
                      </div>

                      <div className="rounded-[16px] bg-[#f5f1ec] p-3">
                        <div className="text-[9px] text-[#93877f]">
                          Углеводы
                        </div>

                        <div className="mt-1 text-[15px] font-black">
                          {selectedNutrition.carbs} г
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="mt-3 rounded-[24px] bg-white p-5">
                  <h3 className="text-[15px] font-black">
                    Состав
                  </h3>

                  <p className="mt-3 text-[13px] leading-6 text-[#514842]">
                    {selectedProductDetails?.ingredients ||
                      "Состав уточняется."}
                  </p>
                </div>

                <div className="mt-3 rounded-[24px] bg-white p-5">
                  <h3 className="text-[15px] font-black">
                    Аллергены
                  </h3>

                  {selectedAllergens.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedAllergens.map(
                        (allergen) => (
                          <span
                            key={allergen}
                            className="rounded-full bg-[#f3eee8] px-3 py-2 text-[11px] font-bold"
                          >
                            {allergen}
                          </span>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="mt-3 text-[12px] text-[#81756e]">
                      Основные аллергены в базовом составе не указаны.
                    </p>
                  )}
                </div>

                <div className="mt-3 rounded-[20px] bg-[#fff4d2] p-4">
                  <p className="text-[10px] leading-4 text-[#75613d]">
                    Данные сейчас справочные. Точная пищевая ценность и состав могут отличаться в зависимости от используемых ингредиентов, размера порции и добавок. Перед коммерческим запуском данные сверим с технологическими картами CoffeeTrack.
                  </p>
                </div>

              </div>
            </div>
          </div>
        ) : null}

        {cartOpen ? (
          <div
            data-coffeetrack="COFFEETRACK_MOBILE_CART_DVH_V316"
            className="fixed inset-0 z-[85] flex overflow-hidden justify-center bg-[#faf8f5] md:items-center md:bg-[#5b514a]/35 md:p-6 md:backdrop-blur-[2px]"
          >
            <div className="flex h-screen h-[100dvh] min-h-0 w-full max-w-[520px] flex-col md:h-[760px] md:max-h-[90vh] md:max-w-[980px] md:overflow-hidden md:rounded-[30px] md:bg-[#faf8f5] md:shadow-[0_28px_90px_rgba(60,45,35,.22)]">
              <header className="flex shrink-0 items-center justify-between border-b border-[#e7dfd8] bg-[#faf8f5] px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] text-[#211914] md:px-6 md:pb-5 md:pt-5">
                <button
                  onClick={() => setCartOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e4ddd6] bg-white"
                >
                  <ArrowLeft size={19} />
                </button>

                {/* COFFEETRACK_CART_COUNT_TITLE_V39 */}
                <h2 className="text-[18px] font-black">
                  Корзина · {cartCount}
                </h2>

                <button
                  onClick={() => {
                    setCart([]);
                    setCartOpen(false);
                  }}
                  className="text-[11px] font-bold text-[#a45b49]"
                >
                  Очистить
                </button>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(96px+env(safe-area-inset-bottom))] pt-4 md:px-6 md:pb-6 md:pt-6">
                <button
                  onClick={() => {
                    setCartOpen(false);
                    navigateTab("points");
                  }}
                  className="flex w-full items-center justify-between rounded-[20px] bg-white p-4 text-left"
                >
                  <div>
                    <div className="text-[10px] font-black text-[#968a82]">
                      Забрать в
                    </div>

                    <div className="mt-1 text-[14px] font-black">
                      {pickupPoint.shortName}
                    </div>
                  </div>

                  <ChevronRight size={18} />
                </button>

                <div className="mt-3 space-y-3 md:grid md:grid-cols-2 md:gap-3 md:space-y-0">
                  {cart.map((line) => (
                    <div
                      key={line.key}
                      className="flex gap-3 rounded-[22px] bg-white p-3"
                    >
                      <ProductPhoto
                        productId={line.productId}
                        fallback={line.image}
                        alt={line.name}
                        className="h-24 w-24 rounded-[17px] object-cover"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-[13px] font-black">
                              {line.name}
                            </h3>

                            {line.sizeLabel ? (
                              <div className="mt-1 text-[10px] text-[#8d8179]">
                                {line.sizeLabel}
                              </div>
                            ) : null}

                            {line.addons.length ? (
                              <div className="mt-1 line-clamp-2 text-[10px] text-[#8d8179]">
                                {line.addons
                                  .map(
                                    (addon) =>
                                      addon.label,
                                  )
                                  .join(", ")}
                              </div>
                            ) : null}
                          </div>

                          <div className="whitespace-nowrap text-[13px] font-black">
                            {money(
                              line.unitPrice *
                                line.qty,
                            )}
                          </div>
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() =>
                              changeQty(line.key, -1)
                            }
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f1ece6]"
                          >
                            <Minus size={14} />
                          </button>

                          <span className="w-6 text-center text-[12px] font-black">
                            {line.qty}
                          </span>

                          <button
                            onClick={() =>
                              changeQty(line.key, 1)
                            }
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f7b733] text-[#211914]"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-[22px] bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-bold text-[#736860]">
                      Сумма заказа
                    </span>

                    <span className="text-[19px] font-black">
                      {money(cartTotal)}
                    </span>
                  </div>

                  {/* COFFEETRACK_CART_CLUB_COPY_V39 */}
                  <div className="mt-3 flex items-center justify-between gap-4 border-t border-[#eee8e2] pt-3">
                    <span className="text-[11px] leading-4 text-[#8c8078]">
                      Кофе учитывается в CoffeeTrack Club после выдачи заказа
                    </span>

                    <span className="shrink-0 rounded-full bg-[#fff1c3] px-2.5 py-1.5 text-[9px] font-black text-[#765b21]">
                      6-й в подарок
                    </span>
                  </div>
                </div>
              </div>

              <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[#e6e0d9] bg-[#faf8f5] px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 md:static md:shrink-0 md:px-6 md:pb-6 md:pt-4">
                <div className="mx-auto max-w-[488px] md:max-w-none">
                  <button
                    onClick={() => {
                      setCartOpen(false);
                      setCheckoutOpen(true);
                    }}
                    disabled={!cart.length}
                    className="flex w-full items-center justify-between rounded-[19px] bg-[#f7b733] px-5 py-4 text-[13px] font-black disabled:opacity-50"
                  >
                    <span>Оформить заказ</span>
                    <span>{money(cartTotal)}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {checkoutOpen ? (
          <div
            data-coffeetrack="COFFEETRACK_MOBILE_CHECKOUT_DVH_V316"
            className="fixed inset-0 z-[90] flex overflow-hidden justify-center bg-[#faf8f5] md:items-center md:bg-[#5b514a]/35 md:p-6 md:backdrop-blur-[2px]"
          >
            <div className="flex h-screen h-[100dvh] min-h-0 w-full max-w-[520px] flex-col md:h-auto md:max-h-[90vh] md:max-w-[760px] md:overflow-hidden md:rounded-[30px] md:bg-[#faf8f5] md:shadow-[0_28px_90px_rgba(60,45,35,.22)]">
              <header className="flex shrink-0 items-center gap-4 border-b border-[#e7dfd8] bg-[#faf8f5] px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] text-[#211914] md:px-6 md:pb-5 md:pt-5">
                <button
                  onClick={() => {
                    setCheckoutOpen(false);
                    setCartOpen(true);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e4ddd6] bg-white"
                >
                  <ArrowLeft size={19} />
                </button>

                <h2 className="text-[18px] font-black">
                  Оформление заказа
                </h2>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(96px+env(safe-area-inset-bottom))] pt-4 md:px-7 md:pb-7 md:pt-6">
                <h3 className="text-[15px] font-black">
                  Точка выдачи
                </h3>

                <button
                  onClick={() => {
                    setCheckoutOpen(false);
                    navigateTab("points");
                  }}
                  className="mt-3 flex w-full items-center gap-3 rounded-[22px] bg-white p-4 text-left"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff1c3]">
                    <MapPin size={20} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-black">
                      {pickupPoint.shortName}
                    </div>

                    <div className="mt-1 text-[10px] text-[#887c74]">
                      {pickupPoint.address}
                    </div>
                  </div>

                  <ChevronRight size={17} />
                </button>

                <h3 className="mt-6 text-[15px] font-black">
                  Получение
                </h3>

                <div className="mt-3 rounded-[22px] border-2 border-[#f7b733] bg-white p-4">
                  <div className="flex items-center gap-3">
                    <ShoppingBag size={20} />

                    <div>
                      <div className="text-[13px] font-black">
                        Заберу с собой
                      </div>

                      <div className="mt-1 text-[10px] text-[#8b7f77]">
                        Назови номер заказа на точке
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="mt-6 text-[15px] font-black">
                  Контактный телефон
                </h3>

                <div className="mt-3 rounded-[22px] bg-white p-4">
                  <label
                    htmlFor="checkout-phone"
                    className="text-[10px] font-bold text-[#8d8179]"
                  >
                    Номер для связи по заказу
                  </label>

                  <input
                    id="checkout-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+7 999 123-45-67"
                    value={customerPhone}
                    readOnly={Boolean(
                      customerSession?.id,
                    )}
                    onChange={(event) => {
                      if (
                        customerSession?.id
                      ) {
                        return;
                      }

                      const next = formatPhone(
                        event.target.value,
                      );

                      setCustomerPhone(next);
                      setPhoneError("");
                    }}
                    className={[
                      "mt-2 h-12 w-full rounded-[16px] border px-4 text-[15px] font-black outline-none transition",
                      customerSession?.id
                        ? "cursor-default border-[#e3ddd6] bg-[#f2eee9] text-[#70655e]"
                        : phoneError
                          ? "border-[#d85b50] bg-[#faf8f5]"
                          : "border-[#e7e0d9] bg-[#faf8f5] focus:border-[#f7b733]",
                    ].join(" ")}
                    data-coffeetrack="COFFEETRACK_CHECKOUT_SESSION_PHONE_V39"
                  />

                  {phoneError ? (
                    <p className="mt-2 text-[10px] font-bold text-[#c64d43]">
                      {phoneError}
                    </p>
                  ) : (
                    <p className="mt-2 text-[9px] leading-4 text-[#988c84]">
                      {customerSession
                        ? "Номер привязан к вашему профилю."
                        : "Регистрация не нужна. Номер сохраним на этом устройстве для следующего заказа."}
                    </p>
                  )}
                </div>

                <h3 className="mt-6 text-[15px] font-black">
                  Оплата
                </h3>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  {(
                    [
                      {
                        id: "card",
                        label: "Карта",
                        caption: "МИР · Visa",
                      },
                      {
                        id: "sbp",
                        label: "СБП",
                        caption: "По QR",
                      },
                      {
                        id: "sberpay",
                        label: "SberPay",
                        caption: "В приложении",
                      },
                    ] as const
                  ).map((method) => {
                    const active =
                      paymentMethod === method.id;

                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() =>
                          setPaymentMethod(method.id)
                        }
                        className={[
                          "touch-manipulation rounded-[19px] border-2 bg-white px-2 py-3.5 text-left transition active:scale-[.97]",
                          active
                            ? "border-[#f7b733] shadow-[0_6px_20px_rgba(247,183,51,.14)]"
                            : "border-transparent",
                        ].join(" ")}
                      >
                        <span className="flex items-center justify-between gap-1">
                          <span className="text-[11px] font-black">
                            {method.label}
                          </span>

                          <span
                            className={[
                              "flex h-4 w-4 items-center justify-center rounded-full border",
                              active
                                ? "border-[#f7b733] bg-[#f7b733]"
                                : "border-[#d9d0c8]",
                            ].join(" ")}
                          >
                            {active ? (
                              <Check
                                size={10}
                                strokeWidth={3}
                                className="text-[#211914]"
                              />
                            ) : null}
                          </span>
                        </span>

                        <span className="mt-1.5 block text-[8px] font-bold text-[#998d85]">
                          {method.caption}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {loyalty?.rewardsAvailable ? (
                  <button
                    type="button"
                    disabled={!rewardCanApply}
                    onClick={() =>
                      setUseLoyaltyReward(
                        (current) =>
                          !current,
                      )
                    }
                    className={[
                      "mt-6 flex w-full items-center justify-between gap-4 rounded-[22px] border-2 p-4 text-left transition active:scale-[.99] disabled:cursor-default disabled:opacity-60",
                      useLoyaltyReward &&
                      rewardCanApply
                        ? "border-[#f7b733] bg-[#fff7dc]"
                        : "border-transparent bg-white",
                    ].join(" ")}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[15px] bg-[#f7b733]">
                        <Gift
                          size={20}
                          strokeWidth={2.4}
                        />
                      </span>

                      <span className="min-w-0">
                        <span className="block text-[12px] font-black">
                          Бесплатный кофе доступен
                        </span>

                        <span className="mt-0.5 block text-[9px] leading-4 text-[#887b72]">
                          {rewardPreviewBasePrice > 0
                            ? `Скидка ${money(
                                rewardPreviewBasePrice,
                              )} на базовую стоимость кофе`
                            : "Добавьте кофе в заказ, чтобы использовать подарок"}
                        </span>
                      </span>
                    </span>

                    <span
                      className={[
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition",
                        useLoyaltyReward &&
                        rewardCanApply
                          ? "border-[#f7b733] bg-[#f7b733]"
                          : "border-[#d8cfc7] bg-white",
                      ].join(" ")}
                    >
                      {useLoyaltyReward &&
                      rewardCanApply ? (
                        <Check
                          size={14}
                          strokeWidth={3}
                        />
                      ) : null}
                    </span>
                  </button>
                ) : null}

                <div className="mt-6 rounded-[22px] bg-white p-4">
                  <div className="flex justify-between">
                    <span className="text-[13px] font-bold">
                      Итого
                    </span>

                    <span className="text-[20px] font-black">
                      {money(checkoutTotal)}
                    </span>
                  </div>

                  {rewardPreviewDiscount > 0 ? (
                    <div className="mt-4 flex items-center justify-between border-t border-[#eee8e2] pt-4">
                      <span className="text-[11px] font-bold text-[#7b6b49]">
                        Подарок CoffeeTrack
                      </span>

                      <span className="text-[12px] font-black text-[#26763d]">
                        −{money(
                          rewardPreviewDiscount,
                        )}
                      </span>
                    </div>
                  ) : null}

                  <div className="mt-4 flex items-center justify-between border-t border-[#eee8e2] pt-4">
                    <span className="text-[11px] text-[#8c8078]">
                      Способ оплаты
                    </span>

                    <span className="text-[11px] font-black">
                      {paymentMethodLabel}
                    </span>
                  </div>

                  {/* COFFEETRACK_TEST_PAYMENT_COPY_V39 */}
                  <p className="mt-3 rounded-[14px] bg-[#fff5d9] px-3 py-2.5 text-[9px] leading-4 text-[#80683e]">
                    Тестовый режим: деньги не списываются. Выбор способа оплаты используется только для проверки оформления заказа.
                  </p>
                </div>
              </div>

              
              <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[#e6e0d9] bg-[#faf8f5] px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 md:static md:shrink-0 md:px-7 md:pb-6 md:pt-4">
                <div className="mx-auto max-w-[488px] md:max-w-none">
                  <button
                    onClick={placeOrder}
                    disabled={placingOrder}
                    className="flex w-full items-center justify-between rounded-[19px] bg-[#f7b733] px-5 py-4 text-[13px] font-black disabled:opacity-60"
                  >
                    <span>
                      {placingOrder
                        ? "Создаём заказ…"
                        : useLoyaltyReward &&
                            rewardCanApply &&
                            checkoutTotal === 0
                          ? "Оформить подарок"
                          : "Оформить тестовый заказ"}
                    </span>

                    <span>
                      {money(checkoutTotal)}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {orderSuccessOpen && currentOrder ? (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-[#5b514a]/35 backdrop-blur-[3px] md:items-center md:p-6">
            {/* COFFEETRACK_SUCCESS_MOBILE_FIT_V316 */}
            <div className="max-h-[92dvh] w-full max-w-[520px] overflow-y-auto overscroll-contain rounded-t-[32px] bg-[#faf8f5] px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-5 md:max-w-[560px] md:rounded-[32px] md:px-7 md:pb-7 md:pt-7 md:shadow-[0_28px_90px_rgba(60,45,35,.22)]">
              <div className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-[#d7d0c8]" />

              <div className="flex flex-col items-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-[#e5f5e8] text-[#26763d]">
                  <Coffee size={30} />
                </span>

                <div className="mt-5 text-[10px] font-black uppercase tracking-[.22em] text-[#8c8078]">
                  Заказ принят
                </div>

                <div className="mt-2 text-[58px] font-black leading-none tracking-[-.06em]">
                  {currentOrder.number}
                </div>

                <h2 className="mt-4 text-[19px] font-black">
                  {statusTitle}
                </h2>

                <p className="mt-2 max-w-[310px] text-[12px] leading-5 text-[#847870]">
                  Следи за статусом во вкладке «Заказы». Когда всё будет готово — просто назови номер на точке.
                </p>

                <div className="mt-5 w-full rounded-[20px] bg-white p-4">
                  <div className="flex justify-between text-[12px]">
                    <span className="text-[#897d75]">
                      Статус
                    </span>

                    <span className="font-black">
                      {statusLabel}
                    </span>
                  </div>

                  {currentOrderRewardDiscount > 0 ? (
                    <div className="mt-3 flex justify-between border-t border-[#eee9e3] pt-3">
                      <span className="font-bold text-[#7b6b49]">
                        Подарок CoffeeTrack
                      </span>

                      <span className="font-black text-[#26763d]">
                        −{money(
                          currentOrderRewardDiscount,
                        )}
                      </span>
                    </div>
                  ) : null}

                  <div className="mt-3 flex justify-between border-t border-[#eee9e3] pt-3">
                    <span className="font-bold">
                      Сумма
                    </span>

                    <span className="font-black">
                      {money(currentOrder.total)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setOrderSuccessOpen(false);
                    navigateTab("orders");
                  }}
                  className="mt-5 w-full rounded-[18px] bg-[#f7b733] px-5 py-4 text-[13px] font-black text-[#211914] transition hover:brightness-[.98] active:scale-[.99]"
                >
                  Открыть заказ
                </button>

                <button
                  onClick={() =>
                    setOrderSuccessOpen(false)
                  }
                  className="mt-2 w-full px-5 py-3 text-[11px] font-bold text-[#81756e]"
                >
                  Вернуться в меню
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
